/**
 * EasyPC Build-a-PC V3
 * Modular PC builder with API-driven product loading,
 * 20-rule compatibility engine, and power calculator.
 *
 * Namespace: window.PCBuilderV3
 * Dependencies: SweetAlert2
 */
(function () {
  'use strict';

  // Security: capture customer data into closure and delete global
  var _pcbCustomer = window.__pcbCustomer
    ? Object.freeze({
        id: window.__pcbCustomer.id,
        email: window.__pcbCustomer.email,
        name: window.__pcbCustomer.name,
        sig: window.__pcbCustomer.sig,
        ts: window.__pcbCustomer.ts
      })
    : null;
  try { delete window.__pcbCustomer; } catch(e) { window.__pcbCustomer = undefined; }

  /* ============================================================
   * CONSTANTS
   * ============================================================ */
  const STORAGE_KEY = 'pcBuilderV3';
  const SCHEMA_VERSION = 1;
  const PAGE_SIZE = 50;
  const DEBOUNCE_MS = 300;
  const CACHE_TTL = 5 * 60 * 1000; // 5 min
  const FILTER_PERSIST_TTL = 24 * 60 * 60 * 1000; // 24 hr
  const CURRENCY = '\u20B1'; // peso sign

  /* ============================================================
   * ComponentRegistry — 16 component type definitions
   * ============================================================ */
  const ComponentRegistry = {
    types: {
      cpu:      { label: 'Processor',        icon: 'cpu',       collection: 'processor',              group: 'core', order: 1,  hasFilters: true },
      cooler:   { label: 'CPU Cooler',        icon: 'cooler',    collection: 'cpu-cooling',            group: 'core', order: 2,  hasFilters: true },
      motherboard:{ label: 'Motherboard',     icon: 'motherboard', collection: 'motherboard',            group: 'core', order: 3,  hasFilters: true },
      ram:      { label: 'Memory',            icon: 'ram',       collection: 'memory',                 group: 'core', order: 4,  hasFilters: true },
      gpu:      { label: 'Graphics Card',     icon: 'gpu',       collection: 'graphic-card',           group: 'core', order: 5,  hasFilters: true },
      ssd:      { label: 'SSD',               icon: 'ssd',       collection: 'solid-state-drive',      group: 'core', order: 6,  hasFilters: true },
      'ssd-sata':{ label: 'SSD (SATA)',       icon: 'ssd',       collection: 'solid-state-drive-sata', group: 'core', order: 7,  hasFilters: true },
      hdd:      { label: 'Hard Disk',         icon: 'hdd',       collection: 'hard-disk',              group: 'core', order: 8,  hasFilters: true },
      psu:      { label: 'Power Supply',      icon: 'psu',       collection: 'power-supply',           group: 'core', order: 9,  hasFilters: true },
      case:     { label: 'Case',              icon: 'case',      collection: 'pc-case',                group: 'core', order: 10, hasFilters: true },
      fan:      { label: 'Case Fan',          icon: 'fan',       collection: 'chasis-fan',             group: 'core', order: 11, hasFilters: false },
      display:  { label: 'Monitor',           icon: 'display',   collection: 'display',                group: 'peripheral', order: 12, hasFilters: false },
      keyboard: { label: 'Keyboard',          icon: 'keyboard',  collection: 'keyboard',               group: 'peripheral', order: 13, hasFilters: false },
      mouse:    { label: 'Mouse',             icon: 'mouse',     collection: 'mouse',                  group: 'peripheral', order: 14, hasFilters: false },
      headset:  { label: 'Headset',           icon: 'headset',   collection: 'headset',                group: 'peripheral', order: 15, hasFilters: false },
      speaker:  { label: 'Speaker',           icon: 'speaker',   collection: 'speaker',                group: 'peripheral', order: 16, hasFilters: false },
      webcam:   { label: 'Webcam',            icon: 'webcam',    collection: 'web-digital-camera',     group: 'peripheral', order: 17, hasFilters: false },
    },

    coreTypes: function () {
      return Object.entries(this.types).filter(function (e) { return e[1].group === 'core'; }).map(function (e) { return e[0]; });
    },

    essentialTypes: function () {
      return ['cpu', 'motherboard', 'ram', 'gpu', 'ssd', 'psu'];
    },

    peripheralTypes: function () {
      return Object.entries(this.types).filter(function (e) { return e[1].group === 'peripheral'; }).map(function (e) { return e[0]; });
    },

    allTypes: function () {
      var self = this;
      return Object.keys(this.types).sort(function (a, b) { return self.types[a].order - self.types[b].order; });
    },

    get: function (type) {
      return this.types[type] || null;
    },

    label: function (type) {
      var t = this.types[type];
      return t ? t.label : type;
    },

    groupLabel: function (type) {
      var t = this.types[type];
      return t ? t.label : type;
    },

    filterConfig: function (type) {
      var configs = {
        cpu:     [{ key: 'brand', label: 'Brand' }, { key: 'cpuSocket', label: 'Socket' }, { key: 'cpuPlatform', label: 'Platform' }],
        cooler:  [{ key: 'brand', label: 'Brand' }, { key: 'coolerType', label: 'Type' }],
        motherboard: [{ key: 'brand', label: 'Brand' }, { key: 'moboSocket', label: 'Socket' }, { key: 'moboChipset', label: 'Chipset' }, { key: 'moboFormFactor', label: 'Form Factor' }, { key: 'moboMemoryType', label: 'Memory Type' }],
        ram:     [{ key: 'brand', label: 'Brand' }, { key: 'ramMemoryType', label: 'Type' }, { key: 'ramCapacityGb', label: 'Capacity' }],
        gpu:     [{ key: 'brand', label: 'Brand' }, { key: 'gpuVramGb', label: 'VRAM' }],
        ssd:     [{ key: 'brand', label: 'Brand' }, { key: 'ssdInterface', label: 'Interface' }, { key: 'ssdCapacityGb', label: 'Capacity' }],
        'ssd-sata': [{ key: 'brand', label: 'Brand' }, { key: 'ssdCapacityGb', label: 'Capacity' }],
        hdd:     [{ key: 'brand', label: 'Brand' }, { key: 'hddCapacityGb', label: 'Capacity' }],
        psu:     [{ key: 'brand', label: 'Brand' }, { key: 'psuWattage', label: 'Wattage' }, { key: 'psuEfficiency', label: 'Efficiency' }, { key: 'psuModular', label: 'Modular' }],
        case:    [{ key: 'brand', label: 'Brand' }, { key: 'caseFormFactors', label: 'Form Factor' }],
      };
      return configs[type] || [{ key: 'brand', label: 'Brand' }];
    }
  };

  /* ============================================================
   * MULTI_QTY_TYPES — Types that support quantity > 1
   * ============================================================ */
  var MULTI_QTY_TYPES = {
    ram: { limitField: 'moboDimmSlots', limitSource: 'motherboard', slotLabel: 'DIMM slots' },
    ssd: { limitField: 'moboM2Slots', limitSource: 'motherboard', slotLabel: 'M.2 slots' },
    'ssd-sata': { limitField: 'moboSataPorts', limitSource: 'motherboard', shared: 'hdd', slotLabel: 'SATA ports' },
    hdd: { limitField: 'moboSataPorts', limitSource: 'motherboard', shared: 'ssd-sata', slotLabel: 'SATA ports' },
    fan: { limitField: 'caseFanSlots', limitSource: 'case', slotLabel: 'fan slots' }
  };

  function getMaxQty(type, stateComps) {
    var cfg = MULTI_QTY_TYPES[type];
    if (!cfg) return { max: null, hasData: false, sourceProduct: null };
    var source = stateComps[cfg.limitSource];
    if (!source) return { max: null, hasData: false, sourceProduct: null };
    var raw = source[cfg.limitField];
    var slots = raw ? parseInt(raw) : 0;
    if (!slots) return { max: null, hasData: false, sourceProduct: source.title || null };

    // For RAM: account for modules per kit (e.g. 2x8GB = 2 sticks per kit)
    if (type === 'ram' && source) {
      var mod = (stateComps.ram && stateComps.ram.ramModules) ? (stateComps.ram.ramModules || '').match(/^(\d+)/) : null;
      var sticksPerKit = mod ? parseInt(mod[1]) : 1;
      if (sticksPerKit > 0) return { max: Math.floor(slots / sticksPerKit), hasData: true, sourceProduct: source.title, totalSlots: slots, perKit: sticksPerKit };
    }

    // For fans: account for fans per pack (e.g. 3in1 = 3 fans per pack)
    if (type === 'fan') {
      var fanComp = stateComps.fan;
      var fansPerPack = (fanComp && fanComp.fanPackSize) ? parseInt(fanComp.fanPackSize) : 1;
      if (fansPerPack > 1) return { max: Math.floor(slots / fansPerPack), hasData: true, sourceProduct: source.title, totalSlots: slots, perKit: fansPerPack };
    }

    // For shared SATA types: subtract sibling's usage
    if (cfg.shared) {
      var sibling = stateComps[cfg.shared];
      var sibQty = (sibling && sibling.quantity) ? parseInt(sibling.quantity) : (sibling ? 1 : 0);
      return { max: Math.max(1, slots - sibQty), hasData: true, sourceProduct: source.title, totalSlots: slots, siblingUsed: sibQty };
    }

    return { max: slots, hasData: true, sourceProduct: source.title, totalSlots: slots };
  }

  /* ============================================================
   * SLOT_GROUPS — Component grouping for left panel
   * ============================================================ */
  var SLOT_GROUPS = [
    { id: 'core',     label: 'Core Components',    types: ['cpu', 'motherboard'] },
    { id: 'memory',   label: 'Memory & Storage',   types: ['ram', 'ssd', 'ssd-sata', 'hdd'] },
    { id: 'graphics', label: 'Graphics & Power',   types: ['gpu', 'psu'] },
    { id: 'chassis',  label: 'Chassis & Cooling',  types: ['case', 'cooler', 'fan'] },
    { id: 'periph',   label: 'Peripherals',        types: ['display', 'keyboard', 'mouse', 'headset', 'speaker', 'webcam'] }
  ];

  /* ============================================================
   * WIZARD_STEPS — Stepper step definitions
   * ============================================================ */
  var WIZARD_STEPS = [
    { label: 'Core',     types: ['cpu', 'motherboard'] },
    { label: 'Memory',   types: ['ram'] },
    { label: 'Graphics', types: ['gpu'] },
    { label: 'Storage',  types: ['ssd', 'ssd-sata', 'hdd'] },
    { label: 'Power',    types: ['psu'] },
    { label: 'Case',     types: ['case'] },
    { label: 'Cooling',  types: ['cooler', 'fan'] },
    { label: 'Extras',   types: ['display', 'keyboard', 'mouse', 'headset', 'speaker', 'webcam'] }
  ];

  /* ============================================================
   * ACHIEVEMENTS — Build achievement definitions
   * ============================================================ */
  var ACHIEVEMENTS = [
    { id: 'first',    label: 'First Build',     icon: 'star',    check: function (s) { return Object.keys(s.components).length >= 1; } },
    { id: 'balanced', label: 'Balanced Build',   icon: 'cpu',     check: function (s) { var c = s.components; return c.cpu && c.gpu && c.ram && c.ssd && c.psu; } },
    { id: 'rgb',      label: 'RGB Lord',         icon: 'gpu',     check: function (s) { return false; /* future: detect RGB components */ } }
  ];

  /* ============================================================
   * BuildState — Central state + pub/sub
   * ============================================================ */
  function BuildState() {
    this._subs = {};
    this.state = {
      schemaVersion: SCHEMA_VERSION,
      buildName: 'My Build',
      buildId: null,
      components: {},
      totalPrice: 0,
      totalPower: 0,
      warnings: [],
      tier: null,
    };
  }

  BuildState.prototype.subscribe = function (key, fn) {
    if (!this._subs[key]) this._subs[key] = [];
    this._subs[key].push(fn);
  };

  BuildState.prototype._notify = function (key) {
    var fns = this._subs[key] || [];
    var val = key === '*' ? this.state : this.state[key];
    for (var i = 0; i < fns.length; i++) fns[i](val, key);
    // Also notify wildcard subscribers
    var stars = this._subs['*'] || [];
    for (var j = 0; j < stars.length; j++) stars[j](this.state, key);
  };

  BuildState.prototype.setComponent = function (type, data) {
    data.quantity = data.quantity || 1;
    this.state.components[type] = data;
    this._recalc();
    this._notify('components');
    this.saveToStorage();
  };

  BuildState.prototype.setQuantity = function (type, qty) {
    var comp = this.state.components[type];
    if (!comp) return;
    comp.quantity = Math.max(1, parseInt(qty) || 1);
    this._recalc();
    this._notify('components');
    this.saveToStorage();
  };

  BuildState.prototype.removeComponent = function (type) {
    delete this.state.components[type];
    this._recalc();
    this._notify('components');
    this.saveToStorage();
  };

  BuildState.prototype.setBuildName = function (name) {
    this.state.buildName = name;
    this._notify('buildName');
    this.saveToStorage();
  };

  BuildState.prototype.setBuildId = function (id) {
    this.state.buildId = id;
  };

  BuildState.prototype.clear = function () {
    this.state.components = {};
    this.state.buildName = 'My Build';
    this.state.buildId = null;
    this._recalc();
    this._notify('components');
    this._notify('buildName');
    this.saveToStorage();
  };

  BuildState.prototype._recalc = function () {
    var comps = this.state.components;
    var total = 0;
    for (var k in comps) {
      if (comps.hasOwnProperty(k) && comps[k]) {
        total += (parseFloat(comps[k].price) || 0) * (comps[k].quantity || 1);
      }
    }
    this.state.totalPrice = Math.round(total * 100) / 100;
    this.state.totalPower = PowerCalculator.estimate(comps);
    this.state.warnings = CompatibilityEngine.check(comps);
    this.state.tier = this._calcTier(comps);
    this._notify('totalPrice');
    this._notify('totalPower');
    this._notify('warnings');
    this._notify('tier');
  };

  BuildState.prototype._calcTier = function (comps) {
    var cpu = comps.cpu, gpu = comps.gpu;
    if (!cpu && !gpu) return null;
    var cpuB = (cpu && cpu.cpuBenchmark) || 0;
    var gpuB = (gpu && gpu.gpuBenchmark) || 0;
    var avg = (cpuB + gpuB) / 2;

    // Benchmark-based tiers (Passmark + 3DMark averages)
    if (avg >= 40000) return 'Enthusiast';     // i9-14900K + RTX 4090
    if (avg >= 20000) return 'High End';       // i7-13700K + RTX 4070 Ti
    if (avg >= 10000) return 'Mid-Tier';       // i5-13600K + RTX 4060
    if (avg >= 5000) return 'Entry Level';     // i5-12400F + GTX 1650
    if (avg > 0) return 'Budget Build';        // i3-12100F or lower

    // Fallback to tier metafields
    var cpuT = (cpu && cpu.cpuTier) || '';
    var gpuT = (gpu && gpu.gpuTier) || '';
    if (cpuT === 'enthusiast' || gpuT === 'enthusiast') return 'Enthusiast';
    if (cpuT === 'high' || gpuT === 'high') return 'High End';
    if (cpuT === 'mid' || gpuT === 'mid') return 'Mid-Tier';
    if (cpuT === 'entry' || gpuT === 'entry') return 'Entry Level';
    if (cpuT || gpuT) return 'Budget Build';

    // Price-based fallback (Philippine market)
    var totalPrice = 0;
    for (var k in comps) { if (comps[k]) totalPrice += parseFloat(comps[k].price) || 0; }
    if (totalPrice >= 150000) return 'Enthusiast';
    if (totalPrice >= 80000) return 'High End';
    if (totalPrice >= 45000) return 'Mid-Tier';
    if (totalPrice >= 25000) return 'Entry Level';
    if (totalPrice > 0) return 'Budget Build';
    return null;
  };

  BuildState.prototype.saveToStorage = function () {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
    } catch (e) { /* quota exceeded */ }
  };

  BuildState.prototype.loadFromStorage = function () {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return false;
      var saved = JSON.parse(raw);
      if (saved.schemaVersion !== SCHEMA_VERSION) {
        localStorage.removeItem(STORAGE_KEY);
        return false;
      }
      this.state = saved;
      this._recalc();
      return true;
    } catch (e) {
      return false;
    }
  };

  BuildState.prototype.toApiPayload = function () {
    // Keys to include in metaSnapshot (compatibility-relevant fields only)
    var META_KEYS = [
      'cpuSocket','cpuChipsets','cpuPlatform','cpuMemoryTypes','cpuTdp','cpuTier',
      'moboSocket','moboChipset','moboFormFactor','moboMemoryType','moboDimmSlots','moboMaxMemoryGb','moboM2Slots','moboSataPorts',
      'ramMemoryType','ramFormFactor','ramCapacityGb','ramModules','ramSpeedMhz',
      'gpuMinPsuWatts','gpuTdp','gpuFormFactor','gpuLengthMm','gpuSlotCount','gpuTier','gpuVramGb','gpuPowerConnector',
      'ssdInterface','ssdCapacityGb','ssdFormFactor','ssdPcieGen',
      'psuWattage','psuEfficiency','psuModular','psuFormFactor','psuAtxVersion','psuPcieConnectors',
      'caseFormFactors','caseMaxGpuMm','caseMaxCoolerMm','casePsuFormFactor','caseFanSlots',
      'fanPackSize',
      'coolerSockets','coolerType','coolerMaxTdp','coolerHeightMm','coolerRadiatorMm',
      'hddCapacityGb','vendor','shopifyHandle','quantity'
    ];
    var comps = [];
    for (var type in this.state.components) {
      if (!this.state.components.hasOwnProperty(type)) continue;
      var c = this.state.components[type];
      if (!c) continue;
      var snap = {};
      for (var i = 0; i < META_KEYS.length; i++) {
        if (c[META_KEYS[i]] !== undefined && c[META_KEYS[i]] !== null) {
          snap[META_KEYS[i]] = c[META_KEYS[i]];
        }
      }
      var comp = {
        componentType: type,
        shopifyProductId: String(c.shopifyProductId),
        title: c.title,
        price: String(c.price),
        metaSnapshot: snap,
      };
      if (c.shopifyVariantId) comp.shopifyVariantId = String(c.shopifyVariantId);
      if (c.imageUrl) comp.imageUrl = c.imageUrl;
      comps.push(comp);
    }
    return {
      name: this.state.buildName,
      components: comps,
      totalPrice: String(this.state.totalPrice || 0),
      totalPower: this.state.totalPower || 0,
      tier: this.state.tier,
    };
  };

  BuildState.prototype.loadFromApi = function (buildData) {
    this.state.buildId = buildData.id;
    this.state.buildName = buildData.name || 'My Build';
    this.state.components = {};
    if (buildData.components) {
      var validTypes = ComponentRegistry.allTypes();
      for (var i = 0; i < buildData.components.length; i++) {
        var bc = buildData.components[i];
        if (validTypes.indexOf(bc.componentType) === -1) continue;
        var snap = bc.metaSnapshot || {};
        if (typeof snap === 'string') { try { snap = JSON.parse(snap); } catch (e) { snap = {}; } }
        snap.title = bc.title;
        snap.price = bc.price;
        snap.imageUrl = bc.imageUrl;
        snap.shopifyProductId = bc.shopifyProductId;
        snap.shopifyVariantId = bc.shopifyVariantId;
        if (bc.variants) snap.variants = bc.variants;
        this.state.components[bc.componentType] = snap;
      }
    }
    this._recalc();
    this._notify('components');
    this._notify('buildName');
    this.saveToStorage();
  };

  /* ============================================================
   * ApiClient — HTTP client with caching + retry
   * ============================================================ */
  var ApiClient = {
    _baseUrl: '',
    _token: null,
    _cache: {},

    init: function (baseUrl) {
      this._baseUrl = baseUrl.replace(/\/$/, '');
      this._token = null;
      this._cache = {};
      this._loadToken();
    },

    _loadToken: function () {
      try {
        var t = sessionStorage.getItem('pcb_token');
        if (!t) {
          t = localStorage.getItem('pcb_token');
          if (t) {
            sessionStorage.setItem('pcb_token', t);
            localStorage.removeItem('pcb_token');
          }
        }
        if (t) {
          var payload = JSON.parse(atob(t.split('.')[1]));
          if (payload.exp * 1000 > Date.now()) {
            this._token = t;
            return;
          }
        }
      } catch (e) { /* ignore */ }
      this._token = null;
    },

    _saveToken: function (token) {
      this._token = token;
      try {
        sessionStorage.setItem('pcb_token', token);
        localStorage.removeItem('pcb_token');
      } catch (e) { /* */ }
    },

    _headers: function () {
      var h = { 'Content-Type': 'application/json' };
      if (this._token) h['Authorization'] = 'Bearer ' + this._token;
      return h;
    },

    _fetch: function (method, path, body) {
      var url = this._baseUrl + path;
      var h = {};
      if (this._token) h['Authorization'] = 'Bearer ' + this._token;
      if (body) h['Content-Type'] = 'application/json';
      var opts = { method: method, headers: h };
      if (body) opts.body = JSON.stringify(body);
      return fetch(url, opts).then(function (res) {
        if (!res.ok) {
          return res.json().catch(function () { return {}; }).then(function (e) {
            var msg = e.error || e.message || 'API error ' + res.status;
            if (Array.isArray(e.details) && e.details.length) {
              msg += ': ' + e.details.map(function(d) { return d.path + ' - ' + d.message; }).join('; ');
            }
            if (msg.length > 200) msg = msg.substring(0, 200) + '\u2026';
            throw new Error(msg);
          });
        }
        return res.json();
      });
    },

    _cachedGet: function (path) {
      var now = Date.now();
      if (this._cache[path] && now - this._cache[path].ts < CACHE_TTL) {
        return Promise.resolve(this._cache[path].data);
      }
      var self = this;
      return this._fetch('GET', path).then(function (data) {
        self._cache[path] = { data: data, ts: now };
        return data;
      });
    },

    ensureAuth: function () {
      if (this._token) return Promise.resolve();
      var self = this;
      // Check if Shopify customer is logged in (with HMAC signature)
      var cust = _pcbCustomer;
      if (cust && cust.id && cust.sig) {
        return this._fetch('POST', '/api/auth/token', {
          shopifyCustomerId: cust.id,
          email: cust.email || undefined,
          name: cust.name || undefined,
          signature: cust.sig,
          timestamp: cust.ts,
        }).then(function (r) {
          var data = r.data || r;
          self._saveToken(data.token);
        });
      }
      // Anonymous
      return this._fetch('POST', '/api/auth/anonymous', {}).then(function (r) {
        var data = r.data || r;
        self._saveToken(data.token);
      });
    },

    fetchComponents: function (type, params) {
      var qs = '?type=' + encodeURIComponent(type);
      if (params) {
        for (var k in params) {
          if (params.hasOwnProperty(k) && params[k] !== undefined && params[k] !== null && params[k] !== '') {
            qs += '&' + encodeURIComponent(k) + '=' + encodeURIComponent(params[k]);
          }
        }
      }
      return this._cachedGet('/api/components/search' + qs);
    },

    getComponent: function (shopifyProductId) {
      return this._cachedGet('/api/components/' + shopifyProductId);
    },

    saveBuild: function (payload) {
      var self = this;
      return this.ensureAuth().then(function () {
        if (payload.buildId) {
          return self._fetch('PUT', '/api/builds/' + payload.buildId, payload);
        }
        return self._fetch('POST', '/api/builds', payload);
      }).then(function (r) { return r.data || r; });
    },

    loadBuilds: function () {
      var self = this;
      return this.ensureAuth().then(function () {
        return self._fetch('GET', '/api/builds');
      }).then(function (r) { return { builds: r.data || r }; });
    },

    loadBuild: function (id) {
      var self = this;
      return this.ensureAuth().then(function () {
        return self._fetch('GET', '/api/builds/' + id);
      }).then(function (r) { return r.data || r; });
    },

    deleteBuild: function (id) {
      var self = this;
      return this.ensureAuth().then(function () {
        return self._fetch('DELETE', '/api/builds/' + id);
      }).then(function (r) { return r.data || r; });
    },

    shareBuild: function (id) {
      var self = this;
      return this.ensureAuth().then(function () {
        return self._fetch('POST', '/api/builds/' + id + '/share');
      }).then(function (r) { return r.data || r; });
    },

    loadShared: function (token) {
      return this._fetch('GET', '/api/shared/' + token)
        .then(function (r) { return r.data || r; });
    },

    checkCompatibility: function (components) {
      return this._fetch('POST', '/api/compatibility/check', { components: components });
    },

    getRecommendations: function (components, budget) {
      return this._fetch('POST', '/api/recommendations/get', { components: components, budget: budget });
    },

    claimBuilds: function () {
      return this._fetch('POST', '/api/auth/claim')
        .then(function (r) { return r.data || r; });
    },

    submitReport: function (payload) {
      var self = this;
      return this.ensureAuth().then(function () {
        return self._fetch('POST', '/api/reports', payload);
      }).then(function (r) { return r.data || r; });
    }
  };

  /* ============================================================
   * Socket → valid chipsets (source of truth)
   * ============================================================ */
  var SOCKET_CHIPSETS = {
    AM4: ['A320','B350','X370','B450','X470','A520','B550','X570'],
    AM5: ['A620','B650','B650E','X670','X670E','X870','X870E'],
    LGA1700: ['H610','B660','H670','Z690','B760','H770','Z790'],
    LGA1851: ['B860','Z890'],
    LGA1200: ['H410','B460','H470','H510','Z490','B560','H570','Z590'],
    LGA1151: ['H110','B150','H170','Z170','B250','H270','Z270','B360','B365','H310','H370','Z370','Z390'],
    LGA1150: ['H81','B85','H87','Z87','H97','Z97'],
    AM3: ['760G','780G','785G','790GX','790FX','870','880G','890GX','890FX','970','990X','990FX'],
    'FM2+': ['A68H','A78','A88X'],
    sTRX4: ['TRX40'], sWRX8: ['WRX80'], TR4: ['X399']
  };

  /* ============================================================
   * parseConnectors — Parse GPU/PSU power connector strings
   * ============================================================ */
  function parseConnectors(value) {
    if (!value || typeof value !== 'string') return null;
    var s = value.trim();
    var lower = s.toLowerCase();
    if (lower === 'none' || lower === 'none (slot-powered)' || lower === 'n/a' || lower === 'slot-powered') {
      return { native12v2x6: 0, sixPlusTwoPin: 0, raw: s };
    }
    var native12v2x6 = 0;
    var sixPlusTwoPin = 0;
    var segments = s.split(',').map(function(seg) { return seg.trim(); }).filter(Boolean);
    var expanded = [];
    for (var i = 0; i < segments.length; i++) {
      var parts = segments[i].split(/\s+\+\s+/);
      for (var j = 0; j < parts.length; j++) expanded.push(parts[j].trim());
    }
    for (var k = 0; k < expanded.length; k++) {
      var seg = expanded[k];
      var segLower = seg.toLowerCase();
      if (/12v[- ]?2x6|12vhpwr|16[- ]?pin/.test(segLower)) {
        var cm = seg.match(/^(\d+)\s*x?\s/i);
        native12v2x6 += cm ? parseInt(cm[1], 10) : 1;
        continue;
      }
      var pm = seg.match(/(\d+)\s*x\s*(\d\+?\d?)[- ]?pin/i);
      if (pm) {
        sixPlusTwoPin += parseInt(pm[1], 10);
        continue;
      }
      if (/^\d\+?\d?[- ]?pin$/i.test(segLower)) {
        sixPlusTwoPin += 1;
      }
    }
    return { native12v2x6: native12v2x6, sixPlusTwoPin: sixPlusTwoPin, raw: s };
  }

  /* ============================================================
   * CompatibilityEngine — Client-side 21-rule pre-check
   * ============================================================ */
  var CompatibilityEngine = {
    check: function (comps) {
      var warnings = [];
      var cpu = comps.cpu, mobo = comps.motherboard, ram = comps.ram;
      var gpu = comps.gpu, cooler = comps.cooler, psu = comps.psu;
      var cs = comps['case'], ssd = comps.ssd, hdd = comps.hdd;
      var ssdSata = comps['ssd-sata'];

      // Rule 1: CPU-Mobo Socket
      if (cpu && mobo && cpu.cpuSocket && mobo.moboSocket) {
        if (cpu.cpuSocket !== mobo.moboSocket) {
          warnings.push({ severity: 'HARD_BLOCK', rule: 'socket', message: 'CPU socket (' + cpu.cpuSocket + ') does not match motherboard socket (' + mobo.moboSocket + ').' });
        }
      }

      // Rule 2: CPU-Mobo Chipset (socket-based lookup, only when sockets match)
      if (cpu && mobo && cpu.cpuSocket && mobo.moboChipset && cpu.cpuSocket === mobo.moboSocket) {
        var validChipsets = SOCKET_CHIPSETS[cpu.cpuSocket];
        if (validChipsets && validChipsets.indexOf(mobo.moboChipset) === -1) {
          warnings.push({ severity: 'HARD_BLOCK', rule: 'chipset', message: cpu.cpuSocket + ' CPUs work with [' + validChipsets.join(', ') + '] but motherboard has ' + mobo.moboChipset + '.' });
        }
      }

      // Rule 3: DDR Type 3-Way
      if (mobo && ram && mobo.moboMemoryType && ram.ramMemoryType) {
        if (mobo.moboMemoryType.toUpperCase() !== ram.ramMemoryType.toUpperCase()) {
          warnings.push({ severity: 'HARD_BLOCK', rule: 'ddr', message: 'Motherboard uses ' + mobo.moboMemoryType + ' but RAM is ' + ram.ramMemoryType + '.' });
        }
      }
      if (cpu && ram && cpu.cpuMemoryTypes && ram.ramMemoryType) {
        var memTypes = cpu.cpuMemoryTypes.split(',').map(function (s) { return s.trim().toUpperCase(); });
        if (memTypes.indexOf(ram.ramMemoryType.toUpperCase()) === -1) {
          warnings.push({ severity: 'HARD_BLOCK', rule: 'cpu_ddr', message: 'CPU does not support ' + ram.ramMemoryType + '. Supported: ' + cpu.cpuMemoryTypes + '.' });
        }
      }

      // Rule 4: RAM Form Factor
      if (ram && ram.ramFormFactor) {
        if (ram.ramFormFactor.toUpperCase() === 'SODIMM') {
          warnings.push({ severity: 'HARD_BLOCK', rule: 'ram_ff', message: 'SODIMM RAM is for laptops and cannot be used in desktop motherboards.' });
        }
      }

      // Rule 5: Mobo-Case Form Factor
      if (mobo && cs && mobo.moboFormFactor && cs.caseFormFactors) {
        var caseFFs = cs.caseFormFactors.split(',').map(function (s) { return s.trim().toUpperCase(); });
        if (caseFFs.indexOf(mobo.moboFormFactor.trim().toUpperCase()) === -1) {
          warnings.push({ severity: 'HARD_BLOCK', rule: 'form_factor', message: mobo.moboFormFactor + ' motherboard does not fit in this case. Supported: ' + cs.caseFormFactors + '.' });
        }
      }

      // Rule 6: CPU-Cooler Socket
      if (cpu && cooler && cpu.cpuSocket && cooler.coolerSockets) {
        var coolerSocks = cooler.coolerSockets.split(',').map(function (s) { return s.trim().toUpperCase(); });
        var cpuSock = cpu.cpuSocket.trim().toUpperCase();
        // LGA1700 coolers work with LGA1851 (same mounting)
        var match = coolerSocks.indexOf(cpuSock) !== -1;
        if (!match && cpuSock === 'LGA1851') {
          match = coolerSocks.indexOf('LGA1700') !== -1;
        }
        if (!match) {
          warnings.push({ severity: 'HARD_BLOCK', rule: 'cooler_socket', message: 'Cooler does not support ' + cpu.cpuSocket + ' socket. Supported: ' + cooler.coolerSockets + '.' });
        }
      }

      // Rule 8: GPU Length vs Case
      if (gpu && cs && gpu.gpuLengthMm && cs.caseMaxGpuMm) {
        if (parseInt(gpu.gpuLengthMm) > parseInt(cs.caseMaxGpuMm)) {
          warnings.push({ severity: 'HARD_BLOCK', rule: 'gpu_length', message: 'GPU length (' + gpu.gpuLengthMm + 'mm) exceeds case maximum (' + cs.caseMaxGpuMm + 'mm).' });
        }
      }

      // Rule 9: Cooler Height vs Case
      if (cooler && cs && cooler.coolerHeightMm && cs.caseMaxCoolerMm) {
        if (parseInt(cooler.coolerHeightMm) > parseInt(cs.caseMaxCoolerMm)) {
          warnings.push({ severity: 'HARD_BLOCK', rule: 'cooler_height', message: 'Cooler height (' + cooler.coolerHeightMm + 'mm) exceeds case maximum (' + cs.caseMaxCoolerMm + 'mm).' });
        }
      }

      // Rule 10: PSU Form Factor vs Case
      if (psu && cs && psu.psuFormFactor && cs.casePsuFormFactor) {
        if (psu.psuFormFactor.toUpperCase() !== cs.casePsuFormFactor.toUpperCase()) {
          warnings.push({ severity: 'HARD_BLOCK', rule: 'psu_ff', message: psu.psuFormFactor + ' PSU does not fit in ' + cs.casePsuFormFactor + ' case.' });
        }
      }

      // Rule 11: GPU-PSU Wattage
      if (gpu && psu && gpu.gpuMinPsuWatts && psu.psuWattage) {
        if (parseInt(psu.psuWattage) < parseInt(gpu.gpuMinPsuWatts)) {
          warnings.push({ severity: 'HARD_WARN', rule: 'psu_wattage', message: 'GPU recommends ' + gpu.gpuMinPsuWatts + 'W PSU but yours is only ' + psu.psuWattage + 'W.' });
        }
      }

      // Rule 12: RAM Slot Overflow (quantity-aware)
      if (ram && mobo) {
        var ramMatch = (ram.ramModules || '').match(/^(\d+)/);
        var sticksPerKit = ramMatch ? parseInt(ramMatch[1]) : 1;
        var totalSticks = sticksPerKit * (parseInt(ram.quantity) || 1);
        if (mobo.moboDimmSlots) {
          if (totalSticks > parseInt(mobo.moboDimmSlots)) {
            var stkMsg = totalSticks + ' total RAM stick' + (totalSticks > 1 ? 's' : '') + ' (' + sticksPerKit + '-module kit \u00d7 ' + (ram.quantity || 1) + ')';
            warnings.push({ severity: 'HARD_WARN', rule: 'ram_slots', message: stkMsg + ' but motherboard only has ' + mobo.moboDimmSlots + ' DIMM slots.' });
          }
        } else if ((parseInt(ram.quantity) || 1) > 1) {
          warnings.push({ severity: 'SOFT_WARN', rule: 'ram_slots_nodata', message: 'No DIMM slot specs recorded for ' + (mobo.title || 'motherboard') + '. Please verify compatibility before purchasing.' });
        }
      }

      // Rule 13: M.2 Slot Overflow (quantity-aware)
      if (mobo && ssd) {
        var isM2 = ssd.ssdFormFactor && (ssd.ssdFormFactor.indexOf('2280') !== -1 || ssd.ssdFormFactor.indexOf('2242') !== -1 || (ssd.ssdInterface && ssd.ssdInterface.indexOf('M.2') !== -1));
        if (isM2) {
          var m2Count = parseInt(ssd.quantity) || 1;
          if (mobo.moboM2Slots) {
            if (m2Count > parseInt(mobo.moboM2Slots)) {
              warnings.push({ severity: 'HARD_WARN', rule: 'm2_slots', message: m2Count + ' M.2 drive' + (m2Count > 1 ? 's' : '') + ' but motherboard only has ' + mobo.moboM2Slots + ' M.2 slots.' });
            }
          } else if (m2Count > 1) {
            warnings.push({ severity: 'SOFT_WARN', rule: 'm2_slots_nodata', message: 'No M.2 slot specs recorded for ' + (mobo.title || 'motherboard') + '. Please verify compatibility before purchasing.' });
          }
        }
      }

      // Rule 13b: SATA Port Overflow (shared pool: ssd-sata + hdd)
      if (mobo) {
        var sataQty = (comps['ssd-sata'] ? (parseInt(comps['ssd-sata'].quantity) || 1) : 0) + (comps.hdd ? (parseInt(comps.hdd.quantity) || 1) : 0);
        if (sataQty > 1 && mobo.moboSataPorts) {
          if (sataQty > parseInt(mobo.moboSataPorts)) {
            warnings.push({ severity: 'HARD_WARN', rule: 'sata_ports', message: sataQty + ' SATA devices but motherboard only has ' + mobo.moboSataPorts + ' SATA ports.' });
          }
        } else if (sataQty > 1 && !mobo.moboSataPorts) {
          warnings.push({ severity: 'SOFT_WARN', rule: 'sata_ports_nodata', message: 'No SATA port specs recorded for ' + (mobo.title || 'motherboard') + '. Please verify compatibility before purchasing.' });
        }
      }

      // Rule 13c: Fan Slot Overflow (pack-size aware)
      if (comps.fan && cs) {
        var fanQty = parseInt(comps.fan.quantity) || 1;
        var fansPerPack = parseInt(comps.fan.fanPackSize) || 1;
        var totalFans = fanQty * fansPerPack;
        if (cs.caseFanSlots) {
          if (totalFans > parseInt(cs.caseFanSlots)) {
            var fanMsg = totalFans + ' total fan' + (totalFans > 1 ? 's' : '') +
              (fansPerPack > 1 ? ' (' + fansPerPack + '-pack \u00d7 ' + fanQty + ')' : '') +
              ' but case only has ' + cs.caseFanSlots + ' fan slots.';
            warnings.push({ severity: 'HARD_WARN', rule: 'fan_slots', message: fanMsg });
          }
        } else if (fanQty > 1) {
          warnings.push({ severity: 'SOFT_WARN', rule: 'fan_slots_nodata', message: 'No fan slot specs recorded for ' + (cs.title || 'case') + '. Please verify compatibility before purchasing.' });
        }
      }

      // Rule 15: Cooler TDP Inadequacy (severe)
      if (cooler && cpu && cooler.coolerMaxTdp && cpu.cpuTdp) {
        var cTdp = parseInt(cooler.coolerMaxTdp);
        var cpTdp = parseInt(cpu.cpuTdp);
        if (cTdp < cpTdp * 0.7) {
          warnings.push({ severity: 'HARD_WARN', rule: 'cooler_tdp_severe', message: 'Cooler TDP (' + cTdp + 'W) is severely inadequate for CPU (' + cpTdp + 'W). Expect thermal throttling.' });
        }
      }

      // Rule 16: AIO Radiator vs Case
      if (cooler && cs && cooler.coolerRadiatorMm && cs.caseRadiatorSupport) {
        var radSize = cooler.coolerRadiatorMm.toString();
        var supported = cs.caseRadiatorSupport.split(',').map(function (s) { return s.trim(); });
        if (supported.indexOf(radSize) === -1) {
          warnings.push({ severity: 'HARD_WARN', rule: 'radiator', message: 'Case does not support ' + radSize + 'mm radiator. Supported: ' + cs.caseRadiatorSupport + '.' });
        }
      }

      // Rule 17: Total Power Budget
      if (psu && psu.psuWattage) {
        var est = PowerCalculator.estimate(comps);
        var psuW = parseInt(psu.psuWattage);
        if (est > psuW * 0.8) {
          warnings.push({ severity: 'SOFT_WARN', rule: 'power_budget', message: 'Estimated power (' + est + 'W) exceeds 80% of PSU capacity (' + psuW + 'W). PSU will run hot.' });
        }
      }

      // Rule 18: PSU Connector Adapter Warning + Rule 21: Insufficient Connectors
      if (gpu && psu) {
        var gpuConn = parseConnectors(gpu.gpuPowerConnector);
        var psuConn = parseConnectors(psu.psuPcieConnectors);
        var psuAtx = psu.psuAtxVersion || '';

        if (gpuConn) {
          // Rule 18: GPU needs 12V-2x6 but PSU lacks native
          if (gpuConn.native12v2x6 > 0) {
            var psuHasNative12v = psuConn ? psuConn.native12v2x6 > 0 : (psuAtx.indexOf('3.') !== -1);
            if (!psuHasNative12v) {
              warnings.push({ severity: 'SOFT_WARN', rule: 'psu_connector_adapter',
                message: 'GPU needs 12V-2x6 connector but PSU' + (psuAtx ? ' is ' + psuAtx : '') + ' lacks native 12V-2x6. Adapter required (increased risk).' });
            }
          }
          // Rule 21: Not enough PCIe power connectors
          if (psuConn) {
            var gpuNeeds12v = gpuConn.native12v2x6;
            var gpuNeeds8pin = gpuConn.sixPlusTwoPin;
            var psuHas12v = psuConn.native12v2x6;
            var psuHas8pin = psuConn.sixPlusTwoPin;

            var unmet12v = Math.max(0, gpuNeeds12v - psuHas12v);
            var remaining8pin = psuHas8pin - (unmet12v * 2);
            var deficit = gpuNeeds8pin - Math.max(0, remaining8pin);

            if (remaining8pin < 0 || deficit > 0) {
              var gpuNeedsParts = [];
              if (gpuNeeds12v > 0) gpuNeedsParts.push(gpuNeeds12v + 'x 12V-2x6');
              if (gpuNeeds8pin > 0) gpuNeedsParts.push(gpuNeeds8pin + 'x 8-pin');
              var psuHasParts = [];
              if (psuHas12v > 0) psuHasParts.push(psuHas12v + 'x 12V-2x6');
              if (psuHas8pin > 0) psuHasParts.push(psuHas8pin + 'x 6+2-pin');
              if (psuHasParts.length === 0) psuHasParts.push('no PCIe connectors');

              warnings.push({ severity: 'HARD_WARN', rule: 'psu_connector_count',
                message: 'GPU needs ' + gpuNeedsParts.join(' + ') + ' but PSU only has ' + psuHasParts.join(' + ') + '. Cannot power this GPU safely.' });
            }
          }
        }
      }

      // Rule 19: Cooler TDP Marginal
      if (cooler && cpu && cooler.coolerMaxTdp && cpu.cpuTdp) {
        var cTdp2 = parseInt(cooler.coolerMaxTdp);
        var cpTdp2 = parseInt(cpu.cpuTdp);
        if (cTdp2 >= cpTdp2 * 0.7 && cTdp2 < cpTdp2) {
          warnings.push({ severity: 'SOFT_WARN', rule: 'cooler_tdp_marginal', message: 'Cooler TDP (' + cTdp2 + 'W) is marginal for CPU (' + cpTdp2 + 'W). May throttle under sustained load.' });
        }
      }

      // Rule 20: PCIe Gen Mismatch
      if (ssd && mobo && ssd.ssdPcieGen && mobo.moboPcieGen) {
        var ssdGen = parseFloat(ssd.ssdPcieGen);
        var moboGen = parseFloat(mobo.moboPcieGen);
        if (ssdGen > moboGen) {
          warnings.push({ severity: 'INFO', rule: 'pcie_gen', message: 'SSD is PCIe ' + ssd.ssdPcieGen + ' but motherboard supports up to PCIe ' + mobo.moboPcieGen + '. SSD will run at reduced speed.' });
        }
      }

      return warnings;
    },

    // Map rule names to the component types they involve
    _ruleTypes: {
      socket: ['cpu','motherboard'], chipset: ['cpu','motherboard'],
      ddr: ['ram','motherboard'], cpu_ddr: ['ram','cpu'], ram_ff: ['ram'],
      ram_slots: ['ram','motherboard'], ram_slots_nodata: ['ram','motherboard'],
      form_factor: ['case','motherboard'],
      cooler_socket: ['cooler','cpu'], cooler_height: ['cooler','case'],
      cooler_tdp_severe: ['cooler','cpu'], cooler_tdp_marginal: ['cooler','cpu'],
      radiator: ['cooler','case'],
      gpu_length: ['gpu','case'],
      psu_ff: ['psu','case'], psu_wattage: ['psu','gpu'],
      psu_connector_adapter: ['psu','gpu'], psu_connector_count: ['psu','gpu'],
      m2_slots: ['ssd','motherboard'], m2_slots_nodata: ['ssd','motherboard'],
      sata_ports: ['ssd-sata','hdd','motherboard'], sata_ports_nodata: ['ssd-sata','hdd','motherboard'],
      fan_slots: ['fan','case'], fan_slots_nodata: ['fan','case'],
      power_budget: ['psu'], pcie_gen: ['ssd','motherboard']
    },

    preCheckProduct: function (product, comps) {
      // Quick compatibility check for a single product against current build
      var testComps = {};
      for (var k in comps) {
        if (comps.hasOwnProperty(k)) testComps[k] = comps[k];
      }
      testComps[product.type] = product;
      var allWarnings = this.check(testComps);
      // Only show warnings relevant to the product type being evaluated
      var pType = product.type;
      var ruleTypes = this._ruleTypes;
      var w = allWarnings.filter(function (warning) {
        var types = ruleTypes[warning.rule];
        return !types || types.indexOf(pType) !== -1;
      });
      if (!w.length) return { badge: 'compatible', color: 'green', label: 'Compatible' };
      var hasBlock = w.some(function (x) { return x.severity === 'HARD_BLOCK'; });
      var hasHardWarn = w.some(function (x) { return x.severity === 'HARD_WARN'; });
      var hasSoftWarn = w.some(function (x) { return x.severity === 'SOFT_WARN'; });
      if (hasBlock) return { badge: 'incompatible', color: 'red', label: 'Incompatible', warnings: w };
      if (hasHardWarn) return { badge: 'warning', color: 'orange', label: 'Issues', warnings: w };
      if (hasSoftWarn) return { badge: 'caution', color: 'yellow', label: 'Caution', warnings: w };
      return { badge: 'info', color: 'blue', label: 'Note', warnings: w };
    }
  };

  /* ============================================================
   * PowerCalculator — TDP estimation
   * ============================================================ */
  var PowerCalculator = {
    estimate: function (comps) {
      var w = 80; // base: mobo + overhead
      if (comps.cpu && comps.cpu.cpuTdp) w += parseInt(comps.cpu.cpuTdp) || 0;
      if (comps.gpu && comps.gpu.gpuTdp) w += parseInt(comps.gpu.gpuTdp) || 0;
      // RAM: ~5W per stick × quantity
      if (comps.ram && comps.ram.ramModules) {
        var m = (comps.ram.ramModules || '').match(/^(\d+)/);
        var ramSticks = (m ? parseInt(m[1]) : 1) * (parseInt(comps.ram.quantity) || 1);
        w += ramSticks * 5;
      }
      // SSD: NVMe ~8W, SATA ~4W × quantity
      if (comps.ssd) {
        var iface = (comps.ssd.ssdInterface || '').toLowerCase();
        var ssdW = iface.indexOf('nvme') !== -1 ? 8 : 4;
        w += ssdW * (parseInt(comps.ssd.quantity) || 1);
      }
      if (comps['ssd-sata']) w += 4 * (parseInt(comps['ssd-sata'].quantity) || 1);
      if (comps.hdd) w += 10 * (parseInt(comps.hdd.quantity) || 1);
      if (comps.fan) {
        var fpp = parseInt(comps.fan.fanPackSize) || 1;
        w += 5 * fpp * (parseInt(comps.fan.quantity) || 1);
      }
      return w;
    },

    recommended: function (comps) {
      return Math.ceil(this.estimate(comps) * 1.25 / 50) * 50;
    },

    breakdown: function (comps) {
      var items = [];
      items.push({ label: 'Base System', watts: 80 });
      if (comps.cpu && comps.cpu.cpuTdp) items.push({ label: 'CPU', watts: parseInt(comps.cpu.cpuTdp) });
      if (comps.gpu && comps.gpu.gpuTdp) items.push({ label: 'GPU', watts: parseInt(comps.gpu.gpuTdp) });
      if (comps.ram && comps.ram.ramModules) {
        var m = (comps.ram.ramModules || '').match(/^(\d+)/);
        var rSticks = (m ? parseInt(m[1]) : 1) * (parseInt(comps.ram.quantity) || 1);
        items.push({ label: 'RAM (' + rSticks + ' stick' + (rSticks > 1 ? 's' : '') + ')', watts: rSticks * 5 });
      }
      if (comps.ssd) {
        var iface = (comps.ssd.ssdInterface || '').toLowerCase();
        var ssdPer = iface.indexOf('nvme') !== -1 ? 8 : 4;
        var ssdQ = parseInt(comps.ssd.quantity) || 1;
        items.push({ label: 'SSD' + (ssdQ > 1 ? ' (\u00d7' + ssdQ + ')' : ''), watts: ssdPer * ssdQ });
      }
      if (comps['ssd-sata']) {
        var sataQ = parseInt(comps['ssd-sata'].quantity) || 1;
        items.push({ label: 'SSD SATA' + (sataQ > 1 ? ' (\u00d7' + sataQ + ')' : ''), watts: 4 * sataQ });
      }
      if (comps.hdd) {
        var hddQ = parseInt(comps.hdd.quantity) || 1;
        items.push({ label: 'HDD' + (hddQ > 1 ? ' (\u00d7' + hddQ + ')' : ''), watts: 10 * hddQ });
      }
      if (comps.fan) {
        var fanQ = parseInt(comps.fan.quantity) || 1;
        var fpp2 = parseInt(comps.fan.fanPackSize) || 1;
        var totalFanCount = fpp2 * fanQ;
        items.push({ label: 'Fan' + (totalFanCount > 1 ? 's (' + totalFanCount + ')' : ''), watts: 5 * totalFanCount });
      }
      return items;
    }
  };

  /* ============================================================
   * FilterEngine — Multi-select, price range, persistence
   * ============================================================ */
  function FilterEngine(type) {
    this.type = type;
    this.filters = {};
    this.priceMin = null;
    this.priceMax = null;
    this.search = '';
    this.inStockOnly = true;
    this.sort = 'price_asc';
    this.page = 1;
    this._loadPersisted();
  }

  FilterEngine.prototype.setFilter = function (key, values) {
    if (!values || values.length === 0) {
      delete this.filters[key];
    } else {
      this.filters[key] = values;
    }
    this.page = 1;
    this._persist();
  };

  FilterEngine.prototype.setPrice = function (min, max) {
    this.priceMin = min;
    this.priceMax = max;
    this.page = 1;
    this._persist();
  };

  FilterEngine.prototype.setSearch = function (term) {
    this.search = term;
    this.page = 1;
  };

  FilterEngine.prototype.setSort = function (sort) {
    this.sort = sort;
    this.page = 1;
    this._persist();
  };

  FilterEngine.prototype.setInStockOnly = function (val) {
    this.inStockOnly = val;
    this.page = 1;
    this._persist();
  };

  FilterEngine.prototype.nextPage = function () {
    this.page++;
  };

  FilterEngine.prototype.reset = function () {
    this.filters = {};
    this.priceMin = null;
    this.priceMax = null;
    this.search = '';
    this.inStockOnly = true;
    this.sort = 'price_asc';
    this.page = 1;
    this._persist();
  };

  FilterEngine.prototype.toParams = function () {
    var p = {
      page: this.page,
      limit: PAGE_SIZE,
      sort: this.sort,
    };
    if (this.inStockOnly) p.in_stock = 'true';
    if (this.search) p.search = this.search;
    if (this.priceMin) p.min_price = this.priceMin;
    if (this.priceMax) p.max_price = this.priceMax;
    for (var k in this.filters) {
      if (this.filters.hasOwnProperty(k) && this.filters[k].length) {
        p[k] = this.filters[k].join(',');
      }
    }
    return p;
  };

  FilterEngine.prototype._persist = function () {
    try {
      var data = { filters: this.filters, priceMin: this.priceMin, priceMax: this.priceMax, sort: this.sort, inStockOnly: this.inStockOnly, ts: Date.now() };
      localStorage.setItem('pcb_filters_' + this.type, JSON.stringify(data));
    } catch (e) { /* */ }
  };

  FilterEngine.prototype._loadPersisted = function () {
    try {
      var raw = localStorage.getItem('pcb_filters_' + this.type);
      if (!raw) return;
      var data = JSON.parse(raw);
      if (Date.now() - data.ts > FILTER_PERSIST_TTL) {
        localStorage.removeItem('pcb_filters_' + this.type);
        return;
      }
      this.filters = data.filters || {};
      this.priceMin = data.priceMin;
      this.priceMax = data.priceMax;
      this.sort = data.sort || 'price_asc';
      this.inStockOnly = data.inStockOnly !== false;
    } catch (e) { /* */ }
  };

  /* ============================================================
   * UI.Toast — SweetAlert2 wrapper
   * ============================================================ */
  var Toast = {
    show: function (msg, icon) {
      if (typeof Swal !== 'undefined') {
        Swal.fire({ toast: true, position: 'bottom-end', icon: icon || 'success', title: msg, showConfirmButton: false, timer: 3000, timerProgressBar: true });
      }
    },
    success: function (msg) { this.show(msg, 'success'); },
    error: function (msg) { this.show(msg, 'error'); },
    warning: function (msg) { this.show(msg, 'warning'); },
    info: function (msg) { this.show(msg, 'info'); },
  };

  /* ============================================================
   * UI Helpers
   * ============================================================ */
  function esc(str) {
    if (!str) return '';
    var d = document.createElement('div');
    d.appendChild(document.createTextNode(str));
    return d.innerHTML;
  }

  function _escMd(s) {
    return s ? s.replace(/([[\]()\\|`*_~#>+\-!{}])/g, '\\$1') : '';
  }

  function formatPrice(price) {
    var n = parseFloat(price) || 0;
    return CURRENCY + n.toLocaleString('en-PH', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }

  function debounce(fn, ms) {
    var timer;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () { fn.apply(ctx, args); }, ms);
    };
  }

  function severityClass(s) {
    if (s === 'HARD_BLOCK') return 'pcb__badge--red';
    if (s === 'HARD_WARN') return 'pcb__badge--orange';
    if (s === 'SOFT_WARN') return 'pcb__badge--yellow';
    return 'pcb__badge--blue';
  }

  function severityIcon(s) {
    if (s === 'HARD_BLOCK') return '&#10006;';
    if (s === 'HARD_WARN') return '&#9888;';
    if (s === 'SOFT_WARN') return '&#9888;';
    return '&#8505;';
  }

  /* ============================================================
   * UI.Layout — Main page layout + tabs
   * ============================================================ */
  var Layout = {
    _root: null,
    _state: null,

    init: function (rootEl, state) {
      this._root = rootEl;
      this._root.classList.add('pcb');
      this._state = state;
      this.render();
    },

    render: function () {
      var s = this._state.state;
      var isAdvanced = localStorage.getItem('pcb-mode') === 'advanced';
      this._root.innerHTML =
        // Builder toolbar
        '<div class="pcb__toolbar">' +
          '<div class="pcb__toolbar-inner">' +
            '<div class="pcb__toolbar-title">' +
              '<svg class="pcb__icon"><use href="#pcb-icon-cpu"></use></svg>' +
              ' Build Your PC' +
            '</div>' +
            '<div class="pcb__toolbar-controls">' +
              '<div class="pcb__mode-toggle">' +
                '<span id="pcb-mode-label">Simple</span>' +
                '<div class="pcb__mode-track' + (isAdvanced ? ' active' : '') + '" id="pcb-mode-toggle">' +
                  '<div class="pcb__mode-thumb"></div>' +
                '</div>' +
                '<span id="pcb-mode-label-adv">Advanced</span>' +
              '</div>' +
              '<input type="text" class="pcb__toolbar-build-name" value="' + esc(s.buildName) + '" placeholder="Build name...">' +
              '<div class="pcb__dark-toggle" id="pcb-dark-toggle" title="Toggle dark mode">' +
                '<svg class="pcb__icon" id="pcb-dark-icon"><use href="#pcb-icon-moon"></use></svg>' +
                '<svg class="pcb__icon" id="pcb-light-icon" style="display:none;"><use href="#pcb-icon-sun"></use></svg>' +
              '</div>' +
            '</div>' +
          '</div>' +
        '</div>' +
        // Budget bar
        '<div class="pcb__budget-bar">' +
          '<div class="pcb__budget-inner">' +
            '<div class="pcb__budget-label">Budget</div>' +
            '<div class="pcb__budget-track">' +
              '<div class="pcb__budget-fill" id="pcb-budget-fill"></div>' +
            '</div>' +
            '<div class="pcb__budget-amount" id="pcb-budget-amount">' +
              formatPrice(0) + ' <span>/ ' + formatPrice(200000) + '</span>' +
            '</div>' +
          '</div>' +
        '</div>' +
        // Wizard stepper
        '<div class="pcb__wizard" id="pcb-wizard"></div>' +
        // 3-panel body
        '<div class="pcb__body">' +
          '<div class="pcb__left">' +
            '<div class="pcb__slots" id="pcb-slots"></div>' +
          '</div>' +
          '<div class="pcb__center" id="pcb-center"></div>' +
          '<div class="pcb__right" id="pcb-right"></div>' +
        '</div>';

      // Apply advanced mode class
      if (isAdvanced) this._root.classList.add('pcb--advanced');

      this._bindEvents();
    },

    _bindEvents: function () {
      var self = this;
      // Build name input
      var nameInput = this._root.querySelector('.pcb__toolbar-build-name');
      if (nameInput) {
        nameInput.addEventListener('change', function () {
          self._state.setBuildName(this.value.trim() || 'My Build');
        });
      }
      // Dark mode toggle
      var darkBtn = document.getElementById('pcb-dark-toggle');
      if (darkBtn) {
        darkBtn.addEventListener('click', function () {
          DarkMode.toggle();
        });
      }
      // Simple/Advanced toggle
      var modeToggle = document.getElementById('pcb-mode-toggle');
      if (modeToggle) {
        modeToggle.addEventListener('click', function () {
          var isActive = this.classList.toggle('active');
          var root = document.querySelector('.pcb');
          if (isActive) {
            root.classList.add('pcb--advanced');
            localStorage.setItem('pcb-mode', 'advanced');
          } else {
            root.classList.remove('pcb--advanced');
            localStorage.setItem('pcb-mode', 'simple');
          }
          // Re-render slots to show/hide peripherals
          ComponentSlotList.renderAll();
        });
      }
    }
  };

  /* ============================================================
   * UI.ComponentSlotList — Component slots
   * ============================================================ */
  var ComponentSlotList = {
    _container: null,
    _state: null,

    init: function (state) {
      this._state = state;
      this._container = document.getElementById('pcb-slots');
      this.renderAll();
      var self = this;
      state.subscribe('components', function () { self.renderAll(); });
    },

    renderAll: function () {
      if (!this._container) return;
      var comps = this._state.state.components;
      var html = '';
      for (var g = 0; g < SLOT_GROUPS.length; g++) {
        var group = SLOT_GROUPS[g];
        html += '<div class="pcb__group pcb__group--' + group.id + '">';
        html += '<div class="pcb__group-label">' + esc(group.label) + '</div>';
        for (var t = 0; t < group.types.length; t++) {
          var type = group.types[t];
          var info = ComponentRegistry.get(type);
          if (!info) continue;
          var comp = comps[type];
          html += this._renderSlot(type, info, comp);
        }
        html += '</div>';
      }
      this._container.innerHTML = html;
      this._bindSlotEvents();
    },

    // Keep legacy method for compatibility
    renderTab: function () { this.renderAll(); },

    _renderSlot: function (type, info, comp) {
      if (comp) {
        var metas = this._getSpecTags(type, comp);
        var metaHtml = '';
        if (metas.length) {
          metaHtml = '<div class="pcb__slot-specs">';
          for (var i = 0; i < metas.length; i++) {
            metaHtml += '<span class="pcb__spec-tag">' + esc(metas[i]) + '</span>';
          }
          metaHtml += '</div>';
        }
        // Popular badge for CPU and GPU
        var popularHtml = '';
        if (type === 'cpu' || type === 'gpu') {
          popularHtml = '<div class="pcb__popular-badge">\u2605 Popular Choice</div>';
        }

        // Multi-quantity controls
        var qtyHtml = '';
        var mqCfg = MULTI_QTY_TYPES[type];
        if (mqCfg) {
          var qty = parseInt(comp.quantity) || 1;
          var mqInfo = getMaxQty(type, this._state ? this._state.state.components : {});
          var disablePlus = mqInfo.hasData && mqInfo.max !== null && qty >= mqInfo.max;
          var disableMinus = qty <= 1;
          var slotInfo = '';
          if (mqInfo.hasData && mqInfo.totalSlots) {
            var usedSlots = (mqInfo.perKit > 1) ? qty * mqInfo.perKit : qty;
            slotInfo = '<span class="pcb__qty-slots">' + usedSlots + ' of ' + mqInfo.totalSlots + ' ' + mqCfg.slotLabel + '</span>';
          } else if (mqInfo.sourceProduct) {
            slotInfo = '<span class="pcb__qty-slots pcb__qty-slots--warn">No slot data</span>';
          }
          var priceDisplay = qty > 1 ? formatPrice(comp.price) + ' \u00d7 ' + qty + ' = ' + formatPrice(parseFloat(comp.price) * qty) : formatPrice(comp.price);
          qtyHtml = '<div class="pcb__slot-qty">' +
            '<button class="pcb__qty-btn pcb__qty-btn--minus pcb-qty-change" data-type="' + type + '" data-dir="-1"' + (disableMinus ? ' disabled' : '') + '>\u2212</button>' +
            '<span class="pcb__qty-val">' + qty + '</span>' +
            '<button class="pcb__qty-btn pcb__qty-btn--plus pcb-qty-change" data-type="' + type + '" data-dir="1"' + (disablePlus ? ' disabled' : '') + '>+</button>' +
            slotInfo +
          '</div>';
        }

        var displayPrice = mqCfg && (parseInt(comp.quantity) || 1) > 1
          ? formatPrice(comp.price) + ' \u00d7 ' + (comp.quantity || 1) + ' = ' + formatPrice(parseFloat(comp.price) * (comp.quantity || 1))
          : formatPrice(comp.price);

        return '<div class="pcb__slot pcb__slot--filled" data-type="' + type + '">' +
          '<div class="pcb__slot-icon"><svg class="pcb__icon"><use href="#pcb-icon-' + info.icon + '"></use></svg></div>' +
          '<div class="pcb__slot-info">' +
            '<div class="pcb__slot-label">' + esc(info.label) + (mqCfg && (parseInt(comp.quantity) || 1) > 1 ? ' <span class="pcb__qty-badge">\u00d7' + (comp.quantity || 1) + '</span>' : '') + '</div>' +
            '<div class="pcb__slot-title">' + esc(comp.title) + '</div>' +
            '<div class="pcb__slot-price">' + displayPrice + '</div>' +
            metaHtml +
            qtyHtml +
            popularHtml +
          '</div>' +
          '<div class="pcb__slot-actions">' +
            '<button class="pcb__btn pcb__btn--danger pcb__btn--sm pcb-slot-remove" data-type="' + type + '">&times;</button>' +
          '</div>' +
        '</div>';
      }
      return '<div class="pcb__slot pcb__slot--empty" data-type="' + type + '">' +
        '<div class="pcb__slot-icon"><svg class="pcb__icon"><use href="#pcb-icon-' + info.icon + '"></use></svg></div>' +
        '<div class="pcb__slot-info">' +
          '<div class="pcb__slot-label">' + esc(info.label) + '</div>' +
          '<div class="pcb__slot-hint">+ Select ' + esc(info.label) + '</div>' +
        '</div>' +
      '</div>';
    },

    _getSpecTags: function (type, comp) {
      var tags = [];
      if (type === 'cpu') {
        if (comp.cpuSocket) tags.push(comp.cpuSocket);
        if (comp.cpuTdp) tags.push(comp.cpuTdp + 'W');
      } else if (type === 'motherboard') {
        if (comp.moboSocket) tags.push(comp.moboSocket);
        if (comp.moboChipset) tags.push(comp.moboChipset);
        if (comp.moboFormFactor) tags.push(comp.moboFormFactor);
      } else if (type === 'ram') {
        if (comp.ramMemoryType) tags.push(comp.ramMemoryType);
        if (comp.ramCapacityGb) tags.push(comp.ramCapacityGb + 'GB');
      } else if (type === 'gpu') {
        if (comp.gpuVramGb) tags.push(comp.gpuVramGb + 'GB');
        if (comp.gpuTdp) tags.push(comp.gpuTdp + 'W');
      } else if (type === 'ssd' || type === 'ssd-sata') {
        if (comp.ssdInterface) tags.push(comp.ssdInterface);
        if (comp.ssdCapacityGb) tags.push(comp.ssdCapacityGb + 'GB');
      } else if (type === 'psu') {
        if (comp.psuWattage) tags.push(comp.psuWattage + 'W');
        if (comp.psuEfficiency) tags.push(comp.psuEfficiency);
      } else if (type === 'case') {
        if (comp.caseFormFactors) tags.push(comp.caseFormFactors);
      } else if (type === 'cooler') {
        if (comp.coolerType) tags.push(comp.coolerType);
      }
      return tags;
    },

    _bindSlotEvents: function () {
      var self = this;
      // Remove buttons
      var removeBtns = this._container.querySelectorAll('.pcb-slot-remove');
      for (var i = 0; i < removeBtns.length; i++) {
        removeBtns[i].addEventListener('click', function (e) {
          e.stopPropagation();
          var type = this.getAttribute('data-type');
          self._state.removeComponent(type);
        });
      }
      // Quantity +/- buttons
      var qtyBtns = this._container.querySelectorAll('.pcb-qty-change');
      for (var q = 0; q < qtyBtns.length; q++) {
        qtyBtns[q].addEventListener('click', function (e) {
          e.stopPropagation();
          var type = this.getAttribute('data-type');
          var dir = parseInt(this.getAttribute('data-dir'));
          var comp = self._state.state.components[type];
          if (!comp) return;
          var newQty = (parseInt(comp.quantity) || 1) + dir;
          if (newQty < 1) return;
          self._state.setQuantity(type, newQty);
        });
      }
      // Click on filled slot to change
      var filledSlots = this._container.querySelectorAll('.pcb__slot--filled');
      for (var j = 0; j < filledSlots.length; j++) {
        filledSlots[j].addEventListener('click', function (e) {
          if (e.target.closest('.pcb-slot-remove') || e.target.closest('.pcb-qty-change')) return;
          ProductDrawer.open(this.getAttribute('data-type'));
        });
      }
      // Click on empty slot to select
      var emptySlots = this._container.querySelectorAll('.pcb__slot--empty');
      for (var k = 0; k < emptySlots.length; k++) {
        emptySlots[k].addEventListener('click', function () {
          ProductDrawer.open(this.getAttribute('data-type'));
        });
      }
    }
  };

  /* ============================================================
   * UI.ProductDrawer — Slide-in product selection (replaces modal)
   * ============================================================ */
  var ProductDrawer = {
    _type: null,
    _filter: null,
    _products: [],
    _hasMore: false,
    _loading: false,
    _drawer: null,
    _overlay: null,
    _peek: null,
    _state: null,
    _filtersVisible: false,
    _activeFilterCount: 0,

    init: function (state) {
      this._state = state;
      this._createDrawer();
    },

    _createDrawer: function () {
      var pcbRoot = document.querySelector('.pcb__toolbar');
      var root = pcbRoot ? pcbRoot.parentElement : document.body;

      // Overlay
      var overlay = document.createElement('div');
      overlay.className = 'pcb__drawer-overlay';
      root.appendChild(overlay);
      this._overlay = overlay;

      // Drawer
      var drawer = document.createElement('div');
      drawer.className = 'pcb__drawer';
      drawer.innerHTML =
        '<div class="pcb__drawer-header">' +
          '<h3 id="pcb-drawer-title">Select Component</h3>' +
          '<div class="pcb__drawer-slot-info" id="pcb-drawer-slot-info"></div>' +
          '<button class="pcb__drawer-close" id="pcb-drawer-close">&times;</button>' +
        '</div>' +
        '<div class="pcb__drawer-toolbar">' +
          '<input type="text" class="pcb__drawer-search" id="pcb-drawer-search" placeholder="Search products...">' +
          '<div class="pcb__drawer-toolbar-row">' +
            '<select class="pcb__drawer-sort" id="pcb-drawer-sort">' +
              '<option value="price_asc">Price: Low to High</option>' +
              '<option value="price_desc">Price: High to Low</option>' +
              '<option value="title_asc">Name: A-Z</option>' +
              '<option value="rating_desc">Rating: Best First</option>' +
            '</select>' +
            '<label class="pcb__drawer-stock-toggle"><input type="checkbox" id="pcb-drawer-stock" checked> In Stock</label>' +
            '<button class="pcb__btn pcb__btn--outline pcb__btn--sm" id="pcb-drawer-filter-toggle">Filters</button>' +
          '</div>' +
          '<div class="pcb__drawer-filters" id="pcb-drawer-filters" style="display:none;"></div>' +
        '</div>' +
        '<div class="pcb__drawer-body">' +
          '<div class="pcb__drawer-grid" id="pcb-drawer-grid"></div>' +
          '<div class="pcb__drawer-load-more" id="pcb-drawer-load-more" style="display:none;">' +
            '<button class="pcb__btn pcb__btn--outline pcb__btn--block pcb__btn--sm">Load More</button>' +
          '</div>' +
        '</div>';
      root.appendChild(drawer);
      this._drawer = drawer;

      // Peek tab
      var peek = document.createElement('div');
      peek.className = 'pcb__drawer-peek';
      peek.textContent = 'BROWSE';
      peek.style.display = 'none';
      root.appendChild(peek);
      this._peek = peek;

      this._bindDrawerEvents();
    },

    _bindDrawerEvents: function () {
      var self = this;

      // Close button
      document.getElementById('pcb-drawer-close').addEventListener('click', function () {
        self.close();
      });

      // Overlay click
      this._overlay.addEventListener('click', function () {
        self.close();
      });

      // Peek tab
      this._peek.addEventListener('click', function () {
        if (self._type) self.open(self._type);
      });

      // Search
      document.getElementById('pcb-drawer-search').addEventListener('input', debounce(function () {
        self._filter.setSearch(this.value);
        self._products = [];
        self._loadProducts();
      }, DEBOUNCE_MS));

      // Sort
      document.getElementById('pcb-drawer-sort').addEventListener('change', function () {
        self._filter.setSort(this.value);
        self._products = [];
        self._loadProducts();
      });

      // In-stock toggle
      document.getElementById('pcb-drawer-stock').addEventListener('change', function () {
        self._filter.setInStockOnly(this.checked);
        self._products = [];
        self._loadProducts();
      });

      // Load More
      document.getElementById('pcb-drawer-load-more').addEventListener('click', function () {
        self._filter.nextPage();
        self._loadProducts(true);
      });

      // Filter toggle
      document.getElementById('pcb-drawer-filter-toggle').addEventListener('click', function () {
        self._filtersVisible = !self._filtersVisible;
        document.getElementById('pcb-drawer-filters').style.display = self._filtersVisible ? 'block' : 'none';
      });

      // Escape key
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && self._drawer.classList.contains('pcb__drawer--open')) {
          self.close();
        }
      });
    },

    open: function (type) {
      this._type = type;
      this._filter = new FilterEngine(type);
      this._products = [];
      this._filtersVisible = false;
      var info = ComponentRegistry.get(type);

      // Update drawer title
      document.getElementById('pcb-drawer-title').textContent = 'Select ' + info.label;

      // Slot info note for multi-qty types
      var slotInfoEl = document.getElementById('pcb-drawer-slot-info');
      var mqCfg = MULTI_QTY_TYPES[type];
      if (mqCfg && this._state) {
        var mqInfo = getMaxQty(type, this._state.state.components);
        if (mqInfo.hasData && mqInfo.totalSlots) {
          slotInfoEl.textContent = mqInfo.totalSlots + ' ' + mqCfg.slotLabel + ' available';
          slotInfoEl.className = 'pcb__drawer-slot-info';
          slotInfoEl.style.display = '';
        } else if (mqInfo.sourceProduct) {
          slotInfoEl.textContent = 'No ' + mqCfg.slotLabel + ' data for ' + mqInfo.sourceProduct;
          slotInfoEl.className = 'pcb__drawer-slot-info pcb__drawer-slot-info--warn';
          slotInfoEl.style.display = '';
        } else {
          slotInfoEl.style.display = 'none';
        }
      } else {
        slotInfoEl.style.display = 'none';
      }

      // Reset search/sort UI
      document.getElementById('pcb-drawer-search').value = this._filter.search;
      document.getElementById('pcb-drawer-sort').value = this._filter.sort;
      document.getElementById('pcb-drawer-stock').checked = this._filter.inStockOnly;
      document.getElementById('pcb-drawer-filters').style.display = 'none';

      // Render filters
      this._renderFilters();

      // Load products
      this._loadProducts();

      // Show drawer
      this._drawer.classList.add('pcb__drawer--open');
      this._overlay.classList.add('pcb__drawer-overlay--visible');
      this._peek.style.display = 'none';
    },

    close: function () {
      this._drawer.classList.remove('pcb__drawer--open');
      this._overlay.classList.remove('pcb__drawer-overlay--visible');
      if (this._type) this._peek.style.display = 'block';
    },

    _renderFilters: function () {
      var container = document.getElementById('pcb-drawer-filters');
      var config = ComponentRegistry.filterConfig(this._type);
      var html = '';

      for (var i = 0; i < config.length; i++) {
        var f = config[i];
        html += '<div class="pcb__filter-group" data-filter-key="' + f.key + '">' +
          '<label class="pcb__filter-label">' + esc(f.label) + '</label>' +
          '<div class="pcb__filter-options" id="pcb-filter-' + f.key + '">' +
            '<div class="pcb__filter-loading">Loading...</div>' +
          '</div>' +
        '</div>';
      }

      // Price range
      html += '<div class="pcb__filter-group">' +
        '<label class="pcb__filter-label">Price Range</label>' +
        '<div class="pcb__price-range">' +
          '<input type="number" class="pcb__price-input" id="pcb-price-min" placeholder="Min" value="' + (this._filter.priceMin || '') + '">' +
          '<span class="pcb__price-sep">-</span>' +
          '<input type="number" class="pcb__price-input" id="pcb-price-max" placeholder="Max" value="' + (this._filter.priceMax || '') + '">' +
        '</div>' +
      '</div>';

      // Reset button
      html += '<button class="pcb__btn pcb__btn--outline pcb__btn--sm pcb__btn--block pcb__filter-reset">Reset Filters</button>';

      container.innerHTML = html;
      this._bindFilterEvents();
    },

    _bindFilterEvents: function () {
      var self = this;
      // Price range
      var minInput = document.getElementById('pcb-price-min');
      var maxInput = document.getElementById('pcb-price-max');
      var priceHandler = debounce(function () {
        self._filter.setPrice(minInput.value || null, maxInput.value || null);
        self._products = [];
        self._loadProducts();
      }, DEBOUNCE_MS);
      minInput.addEventListener('input', priceHandler);
      maxInput.addEventListener('input', priceHandler);

      // Reset
      var resetBtn = document.querySelector('.pcb__filter-reset');
      if (resetBtn) {
        resetBtn.addEventListener('click', function () {
          self._filter.reset();
          self._products = [];
          document.getElementById('pcb-drawer-search').value = '';
          document.getElementById('pcb-drawer-sort').value = 'price_asc';
          document.getElementById('pcb-drawer-stock').checked = true;
          self._renderFilters();
          self._loadProducts();
          self._updateFilterCount();
        });
      }
    },

    _loadProducts: function (append) {
      if (this._loading) return;
      this._loading = true;
      var self = this;
      var grid = document.getElementById('pcb-drawer-grid');
      if (!append) {
        grid.innerHTML = '<div class="pcb__drawer-loading">Loading products...</div>';
      }

      var params = this._filter.toParams();
      ApiClient.fetchComponents(this._type, params).then(function (res) {
        self._loading = false;
        if (append) {
          self._products = self._products.concat(res.data || []);
        } else {
          self._products = res.data || [];
        }
        self._hasMore = res.pagination && res.pagination.page < res.pagination.totalPages;
        self._renderProducts();
        self._updateFilterOptions(res.filterValues || {});
      }).catch(function (err) {
        self._loading = false;
        grid.innerHTML = '<div class="pcb__drawer-empty">Failed to load products. ' + esc(err.message) + '</div>';
      });
    },

    _renderProducts: function () {
      var grid = document.getElementById('pcb-drawer-grid');
      if (!this._products.length) {
        grid.innerHTML = '<div class="pcb__drawer-empty">No products found. Try adjusting your filters.</div>';
        document.getElementById('pcb-drawer-load-more').style.display = 'none';
        return;
      }

      var html = '';
      var comps = this._state.state.components;
      for (var i = 0; i < this._products.length; i++) {
        var p = this._products[i];
        var compat = CompatibilityEngine.preCheckProduct(p, comps);
        var isSelected = comps[this._type] && comps[this._type].shopifyProductId === p.shopifyProductId;
        var outOfStock = !p.inStock;
        var hasMultiVariants = p.variants && p.variants.length > 1;
        var cardClass = 'pcb__drawer-card';
        if (isSelected) cardClass += ' pcb__drawer-card--selected';
        if (outOfStock) cardClass += ' pcb__drawer-card--oos';
        if (compat.badge === 'incompatible') cardClass += ' pcb__drawer-card--incompat';
        else if (compat.badge === 'warning') cardClass += ' pcb__drawer-card--warn';

        // For multi-variant, show "from" price
        var priceDisplay = p.price;
        if (hasMultiVariants) {
          var minVPrice = Math.min.apply(null, p.variants.map(function (v) { return parseFloat(v.price); }));
          priceDisplay = String(minVPrice);
        }

        // Build spec tags
        var metas = this._getMetaTags(p);
        var metaHtml = '';
        if (metas.length) {
          metaHtml = '<div class="pcb__drawer-card-meta">';
          for (var mi = 0; mi < metas.length; mi++) {
            metaHtml += '<span class="pcb__spec-tag">' + esc(metas[mi]) + '</span>';
          }
          metaHtml += '</div>';
        }

        // Compat badge
        var badgeHtml = '';
        if (compat.badge !== 'compatible') {
          var badgeClass = compat.warnings && compat.warnings[0] ? severityClass(compat.warnings[0].severity) : 'pcb__badge--info';
          badgeHtml = '<span class="pcb__drawer-card-badge ' + badgeClass + '">' + compat.label + '</span>';
        }

        html += '<div class="' + cardClass + '" data-product-index="' + i + '" style="position:relative;">' +
          '<div class="pcb__drawer-card-img">' +
            (p.imageUrl ? '<img src="' + esc(p.imageUrl) + '" alt="' + esc(p.title) + '" loading="lazy">' : '') +
          '</div>' +
          '<div class="pcb__drawer-card-info">' +
            '<div class="pcb__drawer-card-vendor">' + esc(p.vendor || '') + '</div>' +
            '<div class="pcb__drawer-card-title">' + esc(p.title) + '</div>' +
            metaHtml +
          '</div>' +
          '<div>' +
            '<div class="pcb__drawer-card-price">' + (hasMultiVariants ? 'from ' : '') + formatPrice(priceDisplay) + '</div>' +
            (p.compareAtPrice && parseFloat(p.compareAtPrice) > parseFloat(p.price) ? '<div class="pcb__drawer-card-compare">' + formatPrice(p.compareAtPrice) + '</div>' : '') +
            (outOfStock ? '<div class="pcb__drawer-card-oos">Out of Stock</div>' : '') +
            (hasMultiVariants ? '<div style="font-size:10px;color:var(--pcb-text-muted);">' + p.variants.length + ' options</div>' : '') +
            (!outOfStock && !hasMultiVariants ? '<button class="pcb__btn pcb__btn--accent pcb__btn--sm pcb__drawer-select-btn" data-product-index="' + i + '">Select</button>' : '') +
          '</div>' +
          badgeHtml +
        '</div>';

        // Variant panel for multi-variant products
        if (hasMultiVariants) {
          html += '<div class="pcb__drawer-variants" data-product-index="' + i + '" style="display:none;">';
          for (var vi = 0; vi < p.variants.length; vi++) {
            var v = p.variants[vi];
            var vOos = !v.inStock;
            html += '<div class="pcb__drawer-variant-row' + (vOos ? ' pcb__drawer-variant-row--oos' : '') + '"' +
              ' data-product-index="' + i + '" data-variant-index="' + vi + '">' +
              '<span class="pcb__drawer-variant-title">' + esc(v.title) + '</span>' +
              '<span class="pcb__drawer-variant-price">' + formatPrice(v.price) + '</span>' +
              '<span class="pcb__drawer-variant-stock">' + (vOos ? 'Out of Stock' : 'In Stock') + '</span>' +
              (!vOos ? '<button class="pcb__btn pcb__btn--accent pcb__btn--sm pcb__drawer-variant-select">Select</button>' : '') +
            '</div>';
          }
          html += '</div>';
        }
      }
      grid.innerHTML = html;

      // Load more button
      document.getElementById('pcb-drawer-load-more').style.display = this._hasMore ? 'block' : 'none';

      // Bind card click
      var self = this;
      var cards = grid.querySelectorAll('.pcb__drawer-card');
      for (var j = 0; j < cards.length; j++) {
        cards[j].addEventListener('click', function (e) {
          if (e.target.closest('.pcb__drawer-select-btn')) return;
          var idx = parseInt(this.getAttribute('data-product-index'));
          var product = self._products[idx];
          if (product.variants && product.variants.length > 1) {
            self._toggleVariantPanel(idx);
          } else {
            self._selectProduct(idx);
          }
        });
      }

      // Bind select buttons
      var selectBtns = grid.querySelectorAll('.pcb__drawer-select-btn');
      for (var sb = 0; sb < selectBtns.length; sb++) {
        selectBtns[sb].addEventListener('click', function (e) {
          e.stopPropagation();
          var idx = parseInt(this.getAttribute('data-product-index'));
          self._selectProduct(idx);
        });
      }

      // Bind variant select buttons
      var variantBtns = grid.querySelectorAll('.pcb__drawer-variant-select');
      for (var vb = 0; vb < variantBtns.length; vb++) {
        variantBtns[vb].addEventListener('click', function (e) {
          e.stopPropagation();
          var row = this.closest('.pcb__drawer-variant-row');
          var pIdx = parseInt(row.getAttribute('data-product-index'));
          var vIdx = parseInt(row.getAttribute('data-variant-index'));
          self._selectVariant(pIdx, vIdx);
        });
      }
    },

    _getMetaTags: function (p) {
      var metas = [];
      var type = p.type || this._type;
      if (type === 'cpu') {
        if (p.cpuSocket) metas.push(p.cpuSocket);
        if (p.cpuTdp) metas.push(p.cpuTdp + 'W');
        if (p.cpuBenchmark) metas.push('Score: ' + p.cpuBenchmark);
      } else if (type === 'motherboard') {
        if (p.moboSocket) metas.push(p.moboSocket);
        if (p.moboChipset) metas.push(p.moboChipset);
        if (p.moboFormFactor) metas.push(p.moboFormFactor);
        if (p.moboMemoryType) metas.push(p.moboMemoryType);
      } else if (type === 'ram') {
        if (p.ramMemoryType) metas.push(p.ramMemoryType);
        if (p.ramCapacityGb) metas.push(p.ramCapacityGb + 'GB');
        if (p.ramModules) metas.push(p.ramModules);
        if (p.ramSpeedMhz) metas.push(p.ramSpeedMhz + 'MHz');
      } else if (type === 'gpu') {
        if (p.gpuVramGb) metas.push(p.gpuVramGb + 'GB');
        if (p.gpuTdp) metas.push(p.gpuTdp + 'W');
        if (p.gpuBenchmark) metas.push('Score: ' + p.gpuBenchmark);
      } else if (type === 'ssd' || type === 'ssd-sata') {
        if (p.ssdInterface) metas.push(p.ssdInterface);
        if (p.ssdCapacityGb) metas.push(p.ssdCapacityGb + 'GB');
      } else if (type === 'hdd') {
        if (p.hddCapacityGb) metas.push(p.hddCapacityGb + 'GB');
      } else if (type === 'psu') {
        if (p.psuWattage) metas.push(p.psuWattage + 'W');
        if (p.psuEfficiency) metas.push(p.psuEfficiency);
        if (p.psuModular) metas.push(p.psuModular);
      } else if (type === 'case') {
        if (p.caseFormFactors) metas.push(p.caseFormFactors);
      } else if (type === 'fan') {
        if (p.fanPackSize && parseInt(p.fanPackSize) > 1) metas.push(p.fanPackSize + '-pack');
      } else if (type === 'cooler') {
        if (p.coolerType) metas.push(p.coolerType);
        if (p.coolerMaxTdp) metas.push(p.coolerMaxTdp + 'W');
      }
      return metas;
    },

    _toggleVariantPanel: function (idx) {
      var panels = document.querySelectorAll('.pcb__drawer-variants');
      for (var i = 0; i < panels.length; i++) {
        var panelIdx = parseInt(panels[i].getAttribute('data-product-index'));
        if (panelIdx === idx) {
          panels[i].style.display = panels[i].style.display === 'none' ? 'block' : 'none';
        } else {
          panels[i].style.display = 'none';
        }
      }
    },

    _selectVariant: function (productIdx, variantIdx) {
      var p = this._products[productIdx];
      var v = p.variants[variantIdx];
      if (!v || !v.inStock) {
        Toast.warning('This variant is currently out of stock.');
        return;
      }
      var merged = {};
      for (var k in p) { if (p.hasOwnProperty(k)) merged[k] = p[k]; }
      merged.shopifyVariantId = String(v.shopifyVariantId);
      merged.price = v.price;
      merged.title = p.title + ' - ' + v.title;
      merged.type = merged.type || this._type;
      this._state.setComponent(this._type, merged);
      this.close();
      Toast.success(ComponentRegistry.label(this._type) + ' selected!');
    },

    _selectProduct: function (idx) {
      var p = this._products[idx];
      if (!p) return;
      if (!p.inStock) {
        Toast.warning('This product is currently out of stock.');
        return;
      }
      // Auto-set variant for single-variant products
      if (p.variants && p.variants.length === 1) {
        p.shopifyVariantId = String(p.variants[0].shopifyVariantId);
      }
      // Check for hard blocks
      var self = this;
      var compat = CompatibilityEngine.preCheckProduct(p, this._state.state.components);
      if (compat.badge === 'incompatible') {
        var msgs = compat.warnings.filter(function (w) { return w.severity === 'HARD_BLOCK'; }).map(function (w) { return w.message; });
        if (typeof Swal !== 'undefined') {
          Swal.fire({
            title: 'Incompatible Component',
            html: '<ul style="text-align:left;">' + msgs.map(function (m) { return '<li>' + esc(m) + '</li>'; }).join('') + '</ul>',
            icon: 'error',
            showCancelButton: true,
            confirmButtonText: 'Select Anyway',
            cancelButtonText: 'Cancel',
          }).then(function (result) {
            if (result.isConfirmed) {
              if (p.variants && p.variants.length === 1) {
                p.shopifyVariantId = String(p.variants[0].shopifyVariantId);
              }
              p.type = p.type || self._type;
              self._state.setComponent(self._type, p);
              self.close();
              Toast.success(ComponentRegistry.label(self._type) + ' selected!');
            }
          });
          return;
        }
      }
      p.type = p.type || this._type;
      this._state.setComponent(this._type, p);
      this.close();
      Toast.success(ComponentRegistry.label(this._type) + ' selected!');
    },

    _updateFilterOptions: function (filterValues) {
      var config = ComponentRegistry.filterConfig(this._type);
      var activeCount = 0;
      for (var i = 0; i < config.length; i++) {
        var f = config[i];
        var container = document.getElementById('pcb-filter-' + f.key);
        if (!container) continue;
        var values = filterValues[f.key] || [];
        var selected = this._filter.filters[f.key] || [];
        if (selected.length) activeCount += selected.length;
        if (!values.length) {
          container.innerHTML = '<div class="pcb__filter-empty">-</div>';
          continue;
        }
        var html = '';
        for (var j = 0; j < values.length; j++) {
          var v = values[j];
          var val = typeof v === 'object' ? v.value : v;
          var count = typeof v === 'object' ? v.count : null;
          var checked = selected.indexOf(String(val)) !== -1;
          html += '<label class="pcb__filter-option">' +
            '<input type="checkbox" value="' + esc(String(val)) + '"' + (checked ? ' checked' : '') + '>' +
            '<span>' + esc(String(val)) + (count ? ' (' + count + ')' : '') + '</span>' +
          '</label>';
        }
        container.innerHTML = html;
      }
      this._activeFilterCount = activeCount;
      this._updateFilterCount();
      this._bindFilterCheckboxes();
    },

    _bindFilterCheckboxes: function () {
      var self = this;
      var config = ComponentRegistry.filterConfig(this._type);
      for (var i = 0; i < config.length; i++) {
        (function (f) {
          var container = document.getElementById('pcb-filter-' + f.key);
          if (!container) return;
          var inputs = container.querySelectorAll('input[type="checkbox"]');
          for (var j = 0; j < inputs.length; j++) {
            inputs[j].addEventListener('change', function () {
              var checked = container.querySelectorAll('input[type="checkbox"]:checked');
              var vals = [];
              for (var k = 0; k < checked.length; k++) vals.push(checked[k].value);
              self._filter.setFilter(f.key, vals);
              self._products = [];
              self._loadProducts();
            });
          }
        })(config[i]);
      }
    },

    _updateFilterCount: function () {
      var btn = document.getElementById('pcb-drawer-filter-toggle');
      if (btn) {
        btn.textContent = this._activeFilterCount > 0
          ? 'Filters (' + this._activeFilterCount + ')'
          : 'Filters';
      }
    }
  };

  /* ============================================================
   * Radar Chart Scoring — market-calibrated (2025-2026)
   * Priority: 1) rating metafield  2) benchmark  3) spec fallback
   * ============================================================ */
  function _radarScore(c, type) {
    if (!c) return 0;

    // Priority 1: Use rating metafield (0-100) if populated
    var ratingKey = type + 'Rating';
    var rating = parseInt(c[ratingKey]) || 0;
    if (rating > 0) return Math.min(rating, 100);

    // Priority 2: Use benchmark score if available (CPU/GPU)
    var benchKey = type + 'Benchmark';
    var bench = parseInt(c[benchKey]) || 0;
    if (bench > 0) {
      if (type === 'cpu') {
        // Passmark scale: 10k→15, 20k→35, 30k→50, 40k→65, 55k→80, 70k+→100
        if (bench >= 70000) return 100;
        if (bench >= 55000) return 80 + Math.round((bench - 55000) / 15000 * 20);
        if (bench >= 40000) return 65 + Math.round((bench - 40000) / 15000 * 15);
        if (bench >= 30000) return 50 + Math.round((bench - 30000) / 10000 * 15);
        if (bench >= 20000) return 35 + Math.round((bench - 20000) / 10000 * 15);
        if (bench >= 10000) return 15 + Math.round((bench - 10000) / 10000 * 20);
        return Math.max(5, Math.round(bench / 667));
      }
      if (type === 'gpu') {
        // 3DMark Time Spy: 5k→20, 10k→40, 20k→60, 30k→75, 40k→90, 48k+→100
        if (bench >= 48000) return 100;
        if (bench >= 40000) return 90 + Math.round((bench - 40000) / 8000 * 10);
        if (bench >= 30000) return 75 + Math.round((bench - 30000) / 10000 * 15);
        if (bench >= 20000) return 60 + Math.round((bench - 20000) / 10000 * 15);
        if (bench >= 10000) return 40 + Math.round((bench - 10000) / 10000 * 20);
        if (bench >= 5000) return 20 + Math.round((bench - 5000) / 5000 * 20);
        return Math.max(5, Math.round(bench / 250));
      }
    }

    // Priority 3: Spec-based fallback (market-calibrated)
    switch (type) {
      case 'cpu':
        var p = parseFloat(c.price) || 0;
        if (p >= 40000) return 95;
        if (p >= 25000) return 80;
        if (p >= 15000) return 65;
        if (p >= 8000) return 50;
        if (p >= 5000) return 35;
        return 20;

      case 'ram':
        var gb = parseInt(c.ramCapacityGb) || 0;
        if (gb >= 128) return 100;
        if (gb >= 64) return 90;
        if (gb >= 32) return 70;
        if (gb >= 16) return 50;
        if (gb >= 8) return 25;
        return 10;

      case 'gpu':
        var vram = parseInt(c.gpuVramGb) || 0;
        if (vram >= 24) return 95;
        if (vram >= 16) return 80;
        if (vram >= 12) return 65;
        if (vram >= 8) return 50;
        if (vram >= 6) return 35;
        if (vram >= 4) return 20;
        return 10;

      case 'ssd':
        var cap = parseInt(c.ssdCapacityGb) || 0;
        var nvme = (c.ssdInterface || '').toLowerCase().indexOf('nvme') >= 0;
        var base = 10;
        if (cap >= 4000) base = 95;
        else if (cap >= 2000) base = 75;
        else if (cap >= 1000) base = 60;
        else if (cap >= 512) base = 40;
        else if (cap >= 256) base = 25;
        return Math.min(100, base + (nvme ? 10 : 0));

      case 'psu':
        var w = parseInt(c.psuWattage) || 0;
        if (w >= 1200) return 100;
        if (w >= 1000) return 95;
        if (w >= 850) return 80;
        if (w >= 750) return 70;
        if (w >= 650) return 55;
        if (w >= 550) return 40;
        if (w >= 450) return 25;
        return 15;

      default:
        return 0;
    }
  }

  /* ============================================================
   * UI.BuildSummary — Sidebar summary
   * ============================================================ */
  var BuildSummary = {
    _container: null,
    _state: null,

    init: function (state) {
      this._state = state;
      this._container = document.getElementById('pcb-right');
      this.render();
      var self = this;
      state.subscribe('*', function () { self.render(); });
    },

    render: function () {
      if (!this._container) return;
      var s = this._state.state;
      var comps = s.components;
      var compCount = Object.keys(comps).length;
      var totalTypes = ComponentRegistry.coreTypes().length;

      // Compute build score (average of 5 radar axes)
      var radarScores = [
        _radarScore(comps.cpu, 'cpu'),
        _radarScore(comps.ram, 'ram'),
        _radarScore(comps.gpu, 'gpu'),
        _radarScore(comps.ssd, 'ssd'),
        _radarScore(comps.psu, 'psu')
      ];
      var scoreSum = 0;
      var scoreCount = 0;
      for (var si = 0; si < radarScores.length; si++) {
        if (radarScores[si] > 0) { scoreSum += radarScores[si]; scoreCount++; }
      }
      var buildScore = scoreCount > 0 ? Math.round(scoreSum / scoreCount) : 0;

      // Tier class mapping (new names)
      var tierClassMap = {
        'Enthusiast': 'diamond',
        'High End': 'gold',
        'Mid-Tier': 'platinum',
        'Entry Level': 'silver',
        'Budget Build': 'bronze'
      };
      var tierClass = tierClassMap[s.tier] || '';

      var html = '';

      // Score card
      html += '<div class="pcb__score-card">' +
        '<div class="pcb__score-label">Build Score</div>' +
        '<div class="pcb__score-number" data-target="' + buildScore + '">0<span class="pcb__score-max">/100</span></div>' +
        (s.tier ? '<div class="pcb__score-tier pcb__tier--' + tierClass + '"><svg class="pcb__icon" style="width:14px;height:14px"><use href="#pcb-icon-' + (tierClass === 'diamond' ? 'diamond' : tierClass === 'gold' ? 'crown' : 'star') + '"></use></svg> ' + esc(s.tier) + '</div>' : '');

      // S6: Achievement icons
      html += '<div class="pcb__achievements">';
      for (var ai = 0; ai < ACHIEVEMENTS.length; ai++) {
        var ach = ACHIEVEMENTS[ai];
        var unlocked = ach.check(s);
        html += '<div class="pcb__achievement pcb__achievement--' + (unlocked ? 'unlocked' : 'locked') + '" title="' + esc(ach.label) + '">' +
          '<svg class="pcb__icon"><use href="#pcb-icon-' + ach.icon + '"></use></svg>' +
          '<div class="pcb__achievement-tooltip">' + esc(ach.label) + '</div>' +
        '</div>';
      }
      html += '</div>';
      html += '</div>'; // close score card

      // Radar card
      html += '<div class="pcb__radar-card">' +
        '<h3>Performance Balance</h3>' +
        '<div class="pcb__radar-container" id="pcb-radar"></div>' +
      '</div>';

      // S7: Summary card with per-component price rows
      html += '<div class="pcb__summary-card">' +
        '<h3>Build Summary</h3>' +
        '<div class="pcb__summary-row"><span class="label">Components</span><span class="value">' + compCount + ' / ' + totalTypes + '</span></div>';

      // Per-component rows
      var allTypes = ComponentRegistry.allTypes();
      for (var ci = 0; ci < allTypes.length; ci++) {
        var cType = allTypes[ci];
        var cComp = comps[cType];
        if (cComp) {
          var cQty = parseInt(cComp.quantity) || 1;
          var cLabel = esc(ComponentRegistry.label(cType)) + (cQty > 1 ? ' (\u00d7' + cQty + ')' : '');
          var cPriceDisplay = cQty > 1
            ? formatPrice(cComp.price) + ' \u00d7 ' + cQty + ' = ' + formatPrice(parseFloat(cComp.price) * cQty)
            : formatPrice(cComp.price);
          html += '<div class="pcb__summary-row"><span class="label">' + cLabel + '</span><span class="value">' + cPriceDisplay + '</span></div>';
        }
      }

      // Total row
      html += '<div class="pcb__summary-total"><span>Total</span><span class="price">' + formatPrice(s.totalPrice) + '</span></div>';
      html += '</div>';

      // Warnings
      if (s.warnings.length) {
        html += '<div class="pcb__warnings">';
        for (var i = 0; i < s.warnings.length; i++) {
          var w = s.warnings[i];
          var warnClass = w.severity === 'HARD_BLOCK' ? 'pcb__warning--error' : w.severity === 'HARD_WARN' ? 'pcb__warning--warn' : 'pcb__warning--info';
          html += '<div class="pcb__warning ' + warnClass + '">' +
            '<span class="pcb__warning-icon">' + severityIcon(w.severity) + '</span>' +
            '<span>' + esc(w.message) + '</span>' +
          '</div>';
        }
        html += '</div>';
      }

      // S8: Action buttons (redesigned)
      // Check if the 6 essential types are all filled (CPU, Mobo, RAM, GPU, SSD, PSU)
      var hasEmpty = false;
      var essentialTypes = ComponentRegistry.essentialTypes();
      for (var ei = 0; ei < essentialTypes.length; ei++) {
        if (!comps[essentialTypes[ei]]) { hasEmpty = true; break; }
      }

      html += '<div class="pcb__actions">';
      if (hasEmpty) {
        html += '<button class="pcb__btn pcb__btn--primary pcb__btn--block" id="pcb-add-missing">' +
          '<svg class="pcb__icon" style="width:16px;height:16px;"><use href="#pcb-icon-cpu"></use></svg> Add Missing Components' +
        '</button>';
      } else {
        html += '<button class="pcb__btn pcb__btn--primary pcb__btn--block" id="pcb-add-cart">' +
          '<svg class="pcb__icon" style="width:16px;height:16px;"><use href="#pcb-icon-cart"></use></svg> Add to Cart' +
        '</button>';
      }
      html += '<button class="pcb__btn pcb__btn--outline pcb__btn--block" id="pcb-save">' +
        '<svg class="pcb__icon" style="width:14px;height:14px;"><use href="#pcb-icon-save"></use></svg> Save Build' +
      '</button>' +
      '<button class="pcb__btn pcb__btn--outline pcb__btn--block" id="pcb-share">' +
        '<svg class="pcb__icon" style="width:14px;height:14px;"><use href="#pcb-icon-share"></use></svg> Share' +
      '</button>' +
      '<div class="pcb__btn-row">' +
        '<button class="pcb__btn pcb__btn--outline pcb__btn--sm" id="pcb-load">Load</button>' +
        '<button class="pcb__btn pcb__btn--outline pcb__btn--sm" id="pcb-report">' +
          '<svg class="pcb__icon" style="width:14px;height:14px;"><use href="#pcb-icon-flag"></use></svg> Report' +
        '</button>' +
        '<button class="pcb__btn pcb__btn--danger pcb__btn--sm" id="pcb-clear"><svg class="pcb__icon" style="width:14px;height:14px;"><use href="#pcb-icon-trash"></use></svg> Clear</button>' +
      '</div>';
      html += '</div>';

      // S9: Compare Builds Teaser
      html += '<div class="pcb__compare-teaser">' +
        '<h3>Compare Builds</h3>' +
        '<div class="pcb__compare-thumbs">' +
          '<div class="pcb__compare-thumb">' +
            '<canvas id="pcb-mini-radar-1" width="60" height="60"></canvas>' +
            '<div class="pcb__compare-name">Current Build</div>' +
            '<div class="pcb__compare-score">Score: ' + buildScore + '</div>' +
          '</div>' +
          '<div class="pcb__compare-thumb">' +
            '<canvas id="pcb-mini-radar-2" width="60" height="60"></canvas>' +
            '<div class="pcb__compare-name">Saved Build</div>' +
            '<div class="pcb__compare-score" id="pcb-compare-saved-score">Score: --</div>' +
          '</div>' +
        '</div>' +
        '<button class="pcb__btn pcb__btn--outline pcb__btn--block pcb__btn--sm" id="pcb-compare-btn">Compare Side by Side</button>' +
      '</div>';

      this._container.innerHTML = html;
      this._bindActions();

      // Animate score count-up (0 → target over 2s with ease-out cubic)
      this._animateScoreCountUp();

      // Render radar chart, then animate only the data polygon + dots (not grid/labels)
      if (typeof window.PCBRadarChart !== 'undefined') {
        window.PCBRadarChart.render('pcb-radar', radarScores);
        // Find the data polygon and dots inside the SVG, wrap in a group and animate
        var radarSvg = document.querySelector('#pcb-radar svg');
        if (radarSvg) {
          var polygon = radarSvg.querySelector('polygon[fill*="rgba"]');
          var dots = radarSvg.querySelectorAll('circle');
          if (polygon && dots.length) {
            // Create a <g> wrapper for data elements only
            var ns = 'http://www.w3.org/2000/svg';
            var dataGroup = document.createElementNS(ns, 'g');
            dataGroup.style.transform = 'scale(0)';
            dataGroup.style.transformOrigin = '50% 50%';
            dataGroup.style.opacity = '0';
            dataGroup.style.transition = 'transform 0.8s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.6s ease';
            // Move polygon + dots into the group
            radarSvg.appendChild(dataGroup);
            dataGroup.appendChild(polygon);
            for (var di = 0; di < dots.length; di++) {
              dataGroup.appendChild(dots[di]);
            }
            // Trigger grow-in after a brief delay
            setTimeout(function () {
              dataGroup.style.transform = 'scale(1)';
              dataGroup.style.opacity = '1';
            }, 400);
          }
        }
      }

      // Draw mini radar canvases
      this._drawMiniRadar('pcb-mini-radar-1', radarScores);
      // Draw saved build radar
      var savedScores = this._getSavedBuildScores();
      this._drawMiniRadar('pcb-mini-radar-2', savedScores.scores);
      var savedScoreEl = document.getElementById('pcb-compare-saved-score');
      if (savedScoreEl && savedScores.score > 0) {
        savedScoreEl.textContent = 'Score: ' + savedScores.score;
      }
    },

    _drawMiniRadar: function (canvasId, values) {
      var canvas = document.getElementById(canvasId);
      if (!canvas) return;
      var ctx = canvas.getContext('2d');
      var size = 60;
      var center = size / 2;
      var radius = 22;
      var axes = 5;
      var isDark = document.querySelector('.pcb--dark') !== null;
      var gridColor = isDark ? '#3a3f45' : '#d0d0d0';

      ctx.clearRect(0, 0, size, size);

      // Draw grid
      ctx.strokeStyle = gridColor;
      ctx.lineWidth = 0.5;
      for (var r = 1; r <= 3; r++) {
        ctx.beginPath();
        for (var i = 0; i <= axes; i++) {
          var a = (2 * Math.PI / axes) * i - Math.PI / 2;
          var rr = (r / 3) * radius;
          var x = center + rr * Math.cos(a);
          var y = center + rr * Math.sin(a);
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.stroke();
      }

      // Draw data polygon
      ctx.beginPath();
      ctx.fillStyle = 'rgba(98, 177, 52, 0.25)';
      ctx.strokeStyle = '#62B134';
      ctx.lineWidth = 1.5;
      for (var j = 0; j <= axes; j++) {
        var angle = (2 * Math.PI / axes) * j - Math.PI / 2;
        var val = Math.max((values[j % axes] || 0) / 100, 0.05);
        var px = center + val * radius * Math.cos(angle);
        var py = center + val * radius * Math.sin(angle);
        if (j === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.fill();
      ctx.stroke();
    },

    _getSavedBuildScores: function () {
      try {
        var raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return { scores: [0,0,0,0,0], score: 0 };
        var data = JSON.parse(raw);
        var comps = data.components || {};
        var scores = [
          _radarScore(comps.cpu, 'cpu'),
          _radarScore(comps.ram, 'ram'),
          _radarScore(comps.gpu, 'gpu'),
          _radarScore(comps.ssd, 'ssd'),
          _radarScore(comps.psu, 'psu')
        ];
        var sum = 0; var cnt = 0;
        for (var i = 0; i < scores.length; i++) {
          if (scores[i] > 0) { sum += scores[i]; cnt++; }
        }
        return { scores: scores, score: cnt > 0 ? Math.round(sum / cnt) : 0 };
      } catch (e) {
        return { scores: [0,0,0,0,0], score: 0 };
      }
    },

    _showComparison: function (savedBuildId) {
      var self = this;
      var currentComps = this._state.state.components;
      ApiClient.loadBuild(savedBuildId).then(function (res) {
        var savedBuild = res.build || res;
        var savedComps = {};
        if (savedBuild.components) {
          for (var i = 0; i < savedBuild.components.length; i++) {
            var bc = savedBuild.components[i];
            var snap = bc.metaSnapshot || {};
            if (typeof snap === 'string') { try { snap = JSON.parse(snap); } catch (e) { snap = {}; } }
            snap.title = bc.title;
            snap.price = bc.price;
            snap.imageUrl = bc.imageUrl;
            savedComps[bc.componentType] = snap;
          }
        }

        // Calculate scores
        var currentScores = [
          _radarScore(currentComps.cpu, 'cpu'), _radarScore(currentComps.ram, 'ram'),
          _radarScore(currentComps.gpu, 'gpu'), _radarScore(currentComps.ssd, 'ssd'),
          _radarScore(currentComps.psu, 'psu')
        ];
        var savedScores = [
          _radarScore(savedComps.cpu, 'cpu'), _radarScore(savedComps.ram, 'ram'),
          _radarScore(savedComps.gpu, 'gpu'), _radarScore(savedComps.ssd, 'ssd'),
          _radarScore(savedComps.psu, 'psu')
        ];

        var avgCurrent = 0, avgSaved = 0, cntC = 0, cntS = 0;
        for (var si = 0; si < 5; si++) {
          if (currentScores[si] > 0) { avgCurrent += currentScores[si]; cntC++; }
          if (savedScores[si] > 0) { avgSaved += savedScores[si]; cntS++; }
        }
        avgCurrent = cntC > 0 ? Math.round(avgCurrent / cntC) : 0;
        avgSaved = cntS > 0 ? Math.round(avgSaved / cntS) : 0;

        // Calculate totals
        var totalCurrent = 0, totalSaved = 0;
        var allTypes = ComponentRegistry.coreTypes();
        for (var ti = 0; ti < allTypes.length; ti++) {
          if (currentComps[allTypes[ti]]) totalCurrent += parseFloat(currentComps[allTypes[ti]].price) || 0;
          if (savedComps[allTypes[ti]]) totalSaved += parseFloat(savedComps[allTypes[ti]].price) || 0;
        }

        // Build comparison HTML
        var isDark = document.querySelector('.pcb--dark') !== null;
        var html = '<div style="text-align:left;">';

        // Radar charts row
        html += '<div style="display:flex;justify-content:space-around;margin-bottom:16px;">';
        html += '<div style="text-align:center;"><canvas id="pcb-cmp-radar-1" width="120" height="120"></canvas>';
        html += '<div style="font-weight:600;margin-top:4px;">Current Build</div>';
        html += '<div style="color:#62B134;font-size:18px;font-weight:700;">Score: ' + avgCurrent + '</div></div>';
        html += '<div style="text-align:center;"><canvas id="pcb-cmp-radar-2" width="120" height="120"></canvas>';
        html += '<div style="font-weight:600;margin-top:4px;">' + esc(savedBuild.name || 'Saved Build') + '</div>';
        html += '<div style="color:#62B134;font-size:18px;font-weight:700;">Score: ' + avgSaved + '</div></div>';
        html += '</div>';

        // Component comparison table
        html += '<table style="width:100%;border-collapse:collapse;font-size:13px;">';
        html += '<tr style="border-bottom:2px solid ' + (isDark ? '#3a3f45' : '#e0e0e0') + ';">';
        html += '<th style="text-align:left;padding:6px;">Component</th>';
        html += '<th style="text-align:left;padding:6px;">Current</th>';
        html += '<th style="text-align:left;padding:6px;">Saved</th>';
        html += '<th style="text-align:right;padding:6px;">Diff</th></tr>';

        for (var ci = 0; ci < allTypes.length; ci++) {
          var type = allTypes[ci];
          var cur = currentComps[type];
          var sav = savedComps[type];
          if (!cur && !sav) continue;

          var curPrice = cur ? (parseFloat(cur.price) || 0) : 0;
          var savPrice = sav ? (parseFloat(sav.price) || 0) : 0;
          var diff = curPrice - savPrice;
          var diffColor = diff > 0 ? '#e74c3c' : diff < 0 ? '#27ae60' : (isDark ? '#9aa0a6' : '#666');
          var diffStr = diff === 0 ? '—' : (diff > 0 ? '+' : '') + '₱' + Math.abs(diff).toLocaleString();

          html += '<tr style="border-bottom:1px solid ' + (isDark ? '#2a2d31' : '#f0f0f0') + ';">';
          html += '<td style="padding:6px;font-weight:600;">' + esc(ComponentRegistry.label(type)) + '</td>';
          html += '<td style="padding:6px;max-width:140px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;" title="' + esc(cur ? cur.title : '') + '">' + (cur ? esc((cur.title || '').substring(0, 30)) : '<span style="color:#999;">—</span>') + '</td>';
          html += '<td style="padding:6px;max-width:140px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;" title="' + esc(sav ? sav.title : '') + '">' + (sav ? esc((sav.title || '').substring(0, 30)) : '<span style="color:#999;">—</span>') + '</td>';
          html += '<td style="padding:6px;text-align:right;color:' + diffColor + ';font-weight:600;">' + diffStr + '</td>';
          html += '</tr>';
        }

        // Total row
        var totalDiff = totalCurrent - totalSaved;
        var totalDiffColor = totalDiff > 0 ? '#e74c3c' : totalDiff < 0 ? '#27ae60' : (isDark ? '#9aa0a6' : '#666');
        var totalDiffStr = totalDiff === 0 ? '—' : (totalDiff > 0 ? '+' : '') + '₱' + Math.abs(totalDiff).toLocaleString();
        html += '<tr style="border-top:2px solid ' + (isDark ? '#3a3f45' : '#e0e0e0') + ';font-weight:700;">';
        html += '<td style="padding:8px;">TOTAL</td>';
        html += '<td style="padding:8px;">₱' + totalCurrent.toLocaleString() + '</td>';
        html += '<td style="padding:8px;">₱' + totalSaved.toLocaleString() + '</td>';
        html += '<td style="padding:8px;text-align:right;color:' + totalDiffColor + ';">' + totalDiffStr + '</td>';
        html += '</tr></table></div>';

        Swal.fire({
          title: 'Build Comparison',
          html: html,
          width: 700,
          confirmButtonText: 'Close',
          didOpen: function () {
            self._drawCompareRadar('pcb-cmp-radar-1', currentScores, 120);
            self._drawCompareRadar('pcb-cmp-radar-2', savedScores, 120);
          }
        });
      }).catch(function (err) {
        Toast.error('Failed to load build: ' + err.message);
      });
    },

    _drawCompareRadar: function (canvasId, values, size) {
      var canvas = document.getElementById(canvasId);
      if (!canvas) return;
      var ctx = canvas.getContext('2d');
      var center = size / 2;
      var radius = size / 2 - 15;
      var axes = 5;
      var isDark = document.querySelector('.pcb--dark') !== null;
      var gridColor = isDark ? '#3a3f45' : '#d0d0d0';
      var labelColor = isDark ? '#9aa0a6' : '#666666';
      var labels = ['CPU', 'RAM', 'GPU', 'SSD', 'PSU'];

      ctx.clearRect(0, 0, size, size);

      // Draw grid rings
      ctx.strokeStyle = gridColor;
      ctx.lineWidth = 0.5;
      for (var r = 1; r <= 3; r++) {
        ctx.beginPath();
        for (var i = 0; i <= axes; i++) {
          var a = (2 * Math.PI / axes) * i - Math.PI / 2;
          var rr = (r / 3) * radius;
          var x = center + rr * Math.cos(a);
          var y = center + rr * Math.sin(a);
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.stroke();
      }

      // Axis lines
      ctx.strokeStyle = gridColor;
      ctx.lineWidth = 0.5;
      for (var k = 0; k < axes; k++) {
        var ang = (2 * Math.PI / axes) * k - Math.PI / 2;
        ctx.beginPath();
        ctx.moveTo(center, center);
        ctx.lineTo(center + radius * Math.cos(ang), center + radius * Math.sin(ang));
        ctx.stroke();
      }

      // Labels
      ctx.fillStyle = labelColor;
      ctx.font = '9px Open Sans, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      for (var l = 0; l < axes; l++) {
        var la = (2 * Math.PI / axes) * l - Math.PI / 2;
        var lr = radius + 12;
        ctx.fillText(labels[l], center + lr * Math.cos(la), center + lr * Math.sin(la));
      }

      // Data polygon
      ctx.beginPath();
      ctx.fillStyle = 'rgba(98, 177, 52, 0.25)';
      ctx.strokeStyle = '#62B134';
      ctx.lineWidth = 1.5;
      for (var j = 0; j <= axes; j++) {
        var angle = (2 * Math.PI / axes) * j - Math.PI / 2;
        var val = Math.max((values[j % axes] || 0) / 100, 0.05);
        var px = center + val * radius * Math.cos(angle);
        var py = center + val * radius * Math.sin(angle);
        if (j === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.fill();
      ctx.stroke();

      // Data dots
      ctx.fillStyle = '#62B134';
      for (var d = 0; d < axes; d++) {
        var da = (2 * Math.PI / axes) * d - Math.PI / 2;
        var dv = Math.max((values[d] || 0) / 100, 0.05);
        ctx.beginPath();
        ctx.arc(center + dv * radius * Math.cos(da), center + dv * radius * Math.sin(da), 3, 0, 2 * Math.PI);
        ctx.fill();
      }
    },

    _animateScoreCountUp: function () {
      var el = this._container.querySelector('.pcb__score-number');
      if (!el) return;
      var target = parseInt(el.getAttribute('data-target'), 10) || 0;
      if (target === 0) { el.childNodes[0].textContent = '0'; return; }
      var duration = 2000;
      var startTime = null;
      function step(now) {
        if (!startTime) startTime = now;
        var elapsed = now - startTime;
        var progress = Math.min(elapsed / duration, 1);
        // Ease out cubic
        var eased = 1 - Math.pow(1 - progress, 3);
        var current = Math.round(eased * target);
        el.childNodes[0].textContent = current;
        if (progress < 1) {
          requestAnimationFrame(step);
        }
      }
      // Delay to sync with card appearance
      setTimeout(function () {
        requestAnimationFrame(step);
      }, 300);
    },

    _bindActions: function () {
      var self = this;
      var bind = function (id, fn) {
        var el = document.getElementById(id);
        if (el) el.addEventListener('click', fn);
      };

      bind('pcb-add-cart', function () {
        var warnings = self._state.state.warnings || [];
        var hardBlocks = warnings.filter(function (w) { return w.severity === 'HARD_BLOCK'; });
        var softWarns = warnings.filter(function (w) { return w.severity !== 'HARD_BLOCK'; });

        if (hardBlocks.length > 0 && typeof Swal !== 'undefined') {
          var listHtml = '<div style="text-align:left;max-height:250px;overflow-y:auto;">';
          listHtml += '<div style="margin-bottom:12px;color:#e0e0e0;font-size:13px;">Your build has compatibility issues:</div>';
          for (var i = 0; i < hardBlocks.length; i++) {
            listHtml += '<div style="display:flex;align-items:flex-start;gap:8px;padding:10px 12px;margin-bottom:6px;background:rgba(238,0,0,0.1);border:1px solid rgba(238,0,0,0.3);border-radius:8px;font-size:13px;color:#ff6b6b;">' +
              '<svg style="width:16px;height:16px;flex-shrink:0;margin-top:1px;" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>' +
              '<span>' + esc(hardBlocks[i].message) + '</span></div>';
          }
          for (var j = 0; j < softWarns.length; j++) {
            listHtml += '<div style="display:flex;align-items:flex-start;gap:8px;padding:10px 12px;margin-bottom:6px;background:rgba(245,166,35,0.1);border:1px solid rgba(245,166,35,0.3);border-radius:8px;font-size:13px;color:#f5a623;">' +
              '<svg style="width:16px;height:16px;flex-shrink:0;margin-top:1px;" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>' +
              '<span>' + esc(softWarns[j].message) + '</span></div>';
          }
          listHtml += '</div>';

          Swal.fire({
            title: 'Compatibility Issues Found',
            customClass: { popup: 'pcb-report-modal', backdrop: 'pcb-report-backdrop' },
            html: listHtml,
            showCancelButton: true,
            confirmButtonText: 'Add to Cart Anyway',
            cancelButtonText: 'Go Back & Fix',
            confirmButtonColor: '#62B134',
            icon: 'warning',
          }).then(function (result) {
            if (result.isConfirmed) {
              CartIntegration.addAll(self._state.state.components);
            }
          });
          return;
        }
        CartIntegration.addAll(self._state.state.components);
      });
      bind('pcb-add-missing', function () {
        // Open drawer for first empty essential slot
        var essentialTypes = ComponentRegistry.essentialTypes();
        var comps = self._state.state.components;
        for (var i = 0; i < essentialTypes.length; i++) {
          if (!comps[essentialTypes[i]]) {
            ProductDrawer.open(essentialTypes[i]);
            return;
          }
        }
      });
      bind('pcb-save', function () { ShareManager.save(self._state); });
      bind('pcb-load', function () { ShareManager.showLoadModal(self._state); });
      bind('pcb-share', function () {
        if (typeof Swal === 'undefined') { ShareManager.share(self._state); return; }
        Swal.fire({
          title: 'Share Build',
          customClass: { popup: 'pcb-report-modal', backdrop: 'pcb-report-backdrop' },
          html:
            '<div style="display:flex;flex-direction:column;gap:10px;margin-top:8px;">' +
              '<button id="pcb-share-link" class="pcb__btn pcb__btn--outline pcb__btn--block" style="min-height:48px;font-size:14px;border-color:#444;color:#e0e0e0;">' +
                '<svg class="pcb__icon" style="width:16px;height:16px;margin-right:8px;"><use href="#pcb-icon-share"></use></svg> Share Link' +
              '</button>' +
              '<button id="pcb-share-reddit" class="pcb__btn pcb__btn--outline pcb__btn--block" style="min-height:48px;font-size:14px;border-color:#444;color:#e0e0e0;">' +
                '<svg style="width:16px;height:16px;margin-right:8px;fill:#FF4500;vertical-align:middle;" viewBox="0 0 24 24"><path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/></svg>' +
                ' Reddit Export' +
              '</button>' +
            '</div>',
          showConfirmButton: false,
          showCancelButton: true,
          cancelButtonText: 'Cancel',
          didOpen: function () {
            document.getElementById('pcb-share-link').addEventListener('click', function () {
              Swal.close();
              ShareManager.share(self._state);
            });
            document.getElementById('pcb-share-reddit').addEventListener('click', function () {
              Swal.close();
              RedditExport.export(self._state.state);
            });
          }
        });
      });
      bind('pcb-compare-btn', function () {
        if (typeof Swal === 'undefined') { alert('Compare Builds requires SweetAlert2.'); return; }
        ApiClient.loadBuilds().then(function (res) {
          var builds = res.builds || [];
          if (builds.length === 0) {
            Swal.fire({ title: 'No Saved Builds', text: 'Save a build first to compare.', icon: 'info' });
            return;
          }
          var currentId = self._state.state.buildId;
          var options = {};
          for (var bi = 0; bi < builds.length; bi++) {
            var b = builds[bi];
            if (b.id === currentId) continue;
            var label = esc(b.name || 'Build #' + b.id);
            if (b.tier) label += ' (' + esc(b.tier) + ')';
            if (b.totalPrice) label += ' — ₱' + esc(parseFloat(b.totalPrice).toLocaleString());
            options[b.id] = label;
          }
          if (Object.keys(options).length === 0) {
            Swal.fire({ title: 'No Other Builds', text: 'You need at least one other saved build to compare.', icon: 'info' });
            return;
          }
          Swal.fire({
            title: 'Compare With...',
            input: 'select',
            inputOptions: options,
            inputPlaceholder: 'Select a saved build',
            showCancelButton: true,
            confirmButtonText: 'Compare',
            inputValidator: function (val) { if (!val) return 'Please select a build'; }
          }).then(function (result) {
            if (result.isConfirmed && result.value) {
              self._showComparison(parseInt(result.value, 10));
            }
          });
        }).catch(function (err) {
          Toast.error('Failed to load builds: ' + err.message);
        });
      });
      bind('pcb-clear', function () {
        if (typeof Swal !== 'undefined') {
          Swal.fire({ title: 'Clear Build?', text: 'Remove all selected components?', icon: 'warning', showCancelButton: true, confirmButtonText: 'Clear' })
            .then(function (r) { if (r.isConfirmed) { self._state.clear(); Toast.info('Build cleared.'); } });
        } else {
          self._state.clear();
        }
      });
      bind('pcb-report', function () { ReportManager.showModal(self._state); });
    }
  };

  /* ============================================================
   * UI.PowerBar — Wattage progress bar
   * ============================================================ */
  var PowerBar = {
    render: function (totalPower, comps) {
      if (!totalPower || totalPower <= 80) return '';
      var psu = comps.psu;
      var psuW = psu ? parseInt(psu.psuWattage) || 0 : 0;
      var recommended = PowerCalculator.recommended(comps);
      var maxW = Math.max(psuW, recommended, totalPower) || 1;
      var pct = Math.min(100, Math.round(totalPower / maxW * 100));
      var color = pct < 60 ? '#62B134' : pct < 80 ? '#f0ad4e' : '#d9534f';

      var html = '<div class="pcb__power">' +
        '<div class="pcb__power-header">' +
          '<span class="pcb__power-label">Estimated Power</span>' +
          '<span class="pcb__power-value">' + totalPower + 'W' + (psuW ? ' / ' + psuW + 'W' : '') + '</span>' +
        '</div>' +
        '<div class="pcb__power-bar">' +
          '<div class="pcb__power-fill" style="width:' + pct + '%;background:' + color + ';"></div>' +
        '</div>' +
        '<div class="pcb__power-rec">Recommended PSU: ' + recommended + 'W</div>' +
      '</div>';
      return html;
    }
  };

  /* ============================================================
   * CartIntegration — Shopify /cart/add.js
   * ============================================================ */
  var CartIntegration = {
    /**
     * Resolve shopifyVariantId for a component.
     * 1. Already has variantId → return it
     * 2. Has variants array with entries → use first in-stock
     * 3. Fetch from API by shopifyProductId → pick first in-stock variant
     */
    _resolveVariantId: function (comp) {
      // 1. Already has it
      if (comp.shopifyVariantId && String(comp.shopifyVariantId) !== '0' && String(comp.shopifyVariantId) !== 'undefined') {
        return Promise.resolve(String(comp.shopifyVariantId));
      }
      // 2. Has variants array
      if (comp.variants && comp.variants.length > 0) {
        var v = null;
        for (var i = 0; i < comp.variants.length; i++) {
          if (comp.variants[i].inStock && comp.variants[i].shopifyVariantId) { v = comp.variants[i]; break; }
        }
        if (!v && comp.variants[0].shopifyVariantId) v = comp.variants[0];
        if (v) {
          var vid = String(v.shopifyVariantId);
          if (vid && vid !== '0' && vid !== 'undefined') {
            comp.shopifyVariantId = vid;
            return Promise.resolve(vid);
          }
        }
      }
      // 3. Fetch from API
      if (comp.shopifyProductId) {
        return ApiClient.getComponent(String(comp.shopifyProductId))
          .then(function (res) {
            var data = (res && res.data) ? res.data : res;
            if (data && data.variants && data.variants.length > 0) {
              var picked = null;
              for (var j = 0; j < data.variants.length; j++) {
                if (data.variants[j].inStock && data.variants[j].shopifyVariantId) { picked = data.variants[j]; break; }
              }
              if (!picked && data.variants[0].shopifyVariantId) picked = data.variants[0];
              if (picked) {
                var vid2 = String(picked.shopifyVariantId);
                comp.shopifyVariantId = vid2;
                return vid2;
              }
            }
            return null;
          })
          .catch(function (err) { console.warn('[EasyBuild] Variant resolve failed for product', comp.shopifyProductId, err); return null; });
      }
      return Promise.resolve(null);
    },

    addAll: function (comps) {
      var self = this;
      var types = [];
      for (var type in comps) {
        if (comps.hasOwnProperty(type) && comps[type]) types.push(type);
      }
      if (!types.length) {
        Toast.warning('No components selected.');
        return;
      }

      Toast.info('Preparing cart...');

      // Resolve all variant IDs in parallel
      var promises = types.map(function (type) {
        return self._resolveVariantId(comps[type]).then(function (vid) {
          return { type: type, comp: comps[type], variantId: vid };
        });
      });

      Promise.all(promises).then(function (results) {
        var items = [];
        var missing = [];
        for (var i = 0; i < results.length; i++) {
          var r = results[i];
          if (!r.variantId || r.variantId === '0' || r.variantId === 'undefined' || r.variantId === 'null') {
            missing.push(ComponentRegistry.label(r.type));
            continue;
          }
          items.push({
            id: parseInt(r.variantId),
            quantity: parseInt(r.comp.quantity) || 1,
            type: r.type,
            title: r.comp.title,
            properties: { 'Assemble': 'Yes', 'Build Component': ComponentRegistry.label(r.type), '_pcbuilder': 'v3' }
          });
        }

        if (missing.length > 0 && items.length === 0) {
          Swal.fire({
            title: 'Cannot add to cart',
            html: 'Variant data missing for:<br><b>' + missing.join(', ') + '</b><br><br>Please re-select these components.',
            icon: 'error'
          });
          return;
        }

        if (missing.length > 0) {
          Swal.fire({
            title: 'Some items can\'t be added',
            html: 'Variant data missing for:<br><b>' + missing.join(', ') + '</b><br><br>Add the remaining ' + items.length + ' items?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Add available items',
            cancelButtonText: 'Cancel'
          }).then(function (result) {
            if (result.isConfirmed) self._addSequential(items);
          });
          return;
        }

        self._addSequential(items);
      });
    },

    _addSequential: function (items) {
      var added = 0;
      var addNext = function (idx) {
        if (idx >= items.length) {
          Toast.success(added + ' of ' + items.length + ' components added to cart!');
          setTimeout(function () { window.location.href = '/cart'; }, 1000);
          return;
        }
        var item = items[idx];
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: item.id, quantity: item.quantity || 1, properties: item.properties }] }),
        }).then(function (res) {
          if (!res.ok) throw new Error(item.title || ComponentRegistry.label(item.type));
          added++;
          addNext(idx + 1);
        }).catch(function (err) {
          if (typeof Swal !== 'undefined') {
            Swal.fire({
              title: 'Could not add to cart',
              html: '<b>' + esc(err.message) + '</b> may be out of stock or unavailable.',
              icon: 'warning',
              showCancelButton: true,
              confirmButtonText: 'Skip & Continue',
              cancelButtonText: 'Stop & Replace',
            }).then(function (result) {
              if (result.isConfirmed) addNext(idx + 1);
            });
          } else {
            Toast.error('Failed: ' + err.message + '. Remaining items skipped.');
          }
        });
      };
      addNext(0);
    }
  };

  /* ============================================================
   * RedditExport — Markdown table + clipboard
   * ============================================================ */
  var RedditExport = {
    _formatPrice: function (n) {
      return CURRENCY + Math.round(n).toLocaleString('en-PH');
    },
    _productUrl: function (handle) {
      return handle && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(handle)
        ? 'https://easypc.com.ph/products/' + encodeURIComponent(handle)
        : '';
    },
    _formatDate: function () {
      var m = ['January','February','March','April','May','June','July','August','September','October','November','December'];
      var d = new Date();
      return m[d.getMonth()] + ' ' + d.getDate() + ', ' + d.getFullYear();
    },
    _buildRows: function (state) {
      var rows = [];
      var types = ComponentRegistry.allTypes();
      var total = 0;
      for (var i = 0; i < types.length; i++) {
        var c = state.components[types[i]];
        if (!c) continue;
        var qty = parseInt(c.quantity) || 1;
        var unitPrice = parseFloat(c.price) || 0;
        var lineTotal = unitPrice * qty;
        total += lineTotal;
        rows.push({ label: ComponentRegistry.label(types[i]), title: c.title || '-', handle: c.shopifyHandle, price: unitPrice, quantity: qty, lineTotal: lineTotal });
      }
      return { rows: rows, total: total };
    },
    _toPreviewHtml: function (data) {
      var self = this;
      var h = '<table><thead><tr><th>Type</th><th>Item</th><th style="text-align:right">Price</th></tr></thead><tbody>';
      for (var i = 0; i < data.rows.length; i++) {
        var r = data.rows[i];
        var url = self._productUrl(r.handle);
        var item = url ? '<a href="' + url + '">' + esc(r.title) + '</a>' : esc(r.title);
        var lbl = r.quantity > 1 ? r.label + ' (\u00d7' + r.quantity + ')' : r.label;
        var priceStr = r.quantity > 1 ? self._formatPrice(r.price) + ' \u00d7 ' + r.quantity + ' = ' + self._formatPrice(r.lineTotal) + ' @ EasyPC' : self._formatPrice(r.price) + ' @ EasyPC';
        h += '<tr><td><strong>' + esc(lbl) + '</strong></td><td>' + item + '</td><td style="text-align:right">' + priceStr + '</td></tr>';
      }
      h += '<tr><td></td><td><strong>Subtotal</strong></td><td style="text-align:right"><strong>' + self._formatPrice(data.total) + '</strong></td></tr>';
      h += '</tbody></table>';
      h += '<p style="margin-top:8px;font-size:12px"><a href="https://easypc.com.ph/pages/build-a-pc-beta">EasyPC Build Tool</a> Generated by ' + self._formatDate() + '</p>';
      return h;
    },
    _toMarkdown: function (data) {
      var self = this;
      var lines = [];
      lines.push('');
      lines.push('| Type | Item | Price |');
      lines.push('|:--|:--|--:|');
      for (var i = 0; i < data.rows.length; i++) {
        var r = data.rows[i];
        var url = self._productUrl(r.handle);
        var item = url ? '[' + _escMd(r.title) + '](' + url + ')' : r.title;
        var mdLabel = r.quantity > 1 ? r.label + ' (\u00d7' + r.quantity + ')' : r.label;
        var mdPrice = r.quantity > 1 ? self._formatPrice(r.price) + ' \u00d7 ' + r.quantity + ' = ' + self._formatPrice(r.lineTotal) + ' @ EasyPC' : self._formatPrice(r.price) + ' @ EasyPC';
        lines.push('| **' + mdLabel + '** | ' + item + ' | ' + mdPrice + ' |');
      }
      lines.push('| | **Subtotal** | **' + self._formatPrice(data.total) + '** |');
      lines.push('');
      lines.push('[EasyPC Build Tool](https://easypc.com.ph/pages/build-a-pc-beta) Generated by ' + self._formatDate());
      return lines.join('\n');
    },
    export: function (state) {
      var data = this._buildRows(state);
      var html = this._toPreviewHtml(data);
      var self = this;

      if (typeof Swal !== 'undefined') {
        Swal.fire({
          title: 'Reddit / Share Export',
          customClass: { popup: 'pcb-report-modal', backdrop: 'pcb-report-backdrop' },
          html: '<div style="background:#111;border:1px solid #333;border-radius:8px;padding:12px;max-height:300px;overflow:auto;text-align:left;font-size:13px;color:#e0e0e0;">' + html + '</div>',
          showCancelButton: true,
          confirmButtonText: 'Copy to Clipboard',
          confirmButtonColor: '#62B134',
        }).then(function (result) {
          if (result.isConfirmed) {
            var htmlBlob = new Blob([html], { type: 'text/html' });
            var textBlob = new Blob([self._toMarkdown(data)], { type: 'text/plain' });
            navigator.clipboard.write([new ClipboardItem({ 'text/html': htmlBlob, 'text/plain': textBlob })]).then(function () {
              Toast.success('Copied to clipboard!');
            }).catch(function () {
              navigator.clipboard.writeText(self._toMarkdown(data)).then(function () {
                Toast.success('Copied to clipboard!');
              }).catch(function () {
                Toast.error('Failed to copy.');
              });
            });
          }
        });
      }
    }
  };

  /* ============================================================
   * ReportManager — Bug / Wrong Info reporting
   * ============================================================ */
  var ReportManager = {
    _getBuildContext: function (state) {
      var ctx = { buildName: state.state.name || 'My Build', totalPrice: state.state.totalPrice || 0, components: {} };
      var comps = state.state.components;
      for (var k in comps) {
        if (comps.hasOwnProperty(k) && comps[k]) {
          ctx.components[k] = { title: comps[k].title, price: comps[k].price, shopifyProductId: comps[k].shopifyProductId };
        }
      }
      return ctx;
    },

    showModal: function (state) {
      if (typeof Swal === 'undefined') { Toast.error('Report dialog unavailable.'); return; }
      Swal.fire({
        title: 'Report an Issue',
        customClass: { popup: 'pcb-report-modal', backdrop: 'pcb-report-backdrop' },
        html:
          '<div style="text-align:left;font-size:14px;color:#e0e0e0">' +
          '<label style="display:block;margin-bottom:6px;font-weight:600;color:#ccc">Type</label>' +
          '<select id="swal-report-type" class="swal2-select" style="width:100%;margin-bottom:12px">' +
            '<option value="wrong_specs">Wrong Specs</option>' +
            '<option value="wrong_price">Wrong Price</option>' +
            '<option value="wrong_stock">Wrong Stock</option>' +
            '<option value="compatibility_error">Compatibility Error</option>' +
            '<option value="ui_bug">UI Bug</option>' +
            '<option value="missing_product">Missing Product</option>' +
            '<option value="other">Other</option>' +
          '</select>' +
          '<label style="display:block;margin-bottom:6px;font-weight:600;color:#ccc">Description <span style="color:#777;font-weight:400">(min 10 chars)</span></label>' +
          '<textarea id="swal-report-desc" class="swal2-textarea" style="width:100%;min-height:80px;margin-bottom:12px" placeholder="Describe the issue..."></textarea>' +
          '<label style="display:block;margin-bottom:6px;font-weight:600;color:#ccc">Email <span style="color:#777;font-weight:400">(optional)</span></label>' +
          '<input id="swal-report-email" class="swal2-input" type="email" style="width:100%;margin-bottom:0" placeholder="your@email.com">' +
          '</div>',
        showCancelButton: true,
        confirmButtonText: 'Submit Report',
        confirmButtonColor: '#62B134',
        preConfirm: function () {
          var desc = document.getElementById('swal-report-desc').value.trim();
          var email = document.getElementById('swal-report-email').value.trim();
          if (desc.length < 10) { Swal.showValidationMessage('Description must be at least 10 characters'); return false; }
          if (desc.length > 2000) { Swal.showValidationMessage('Description must be under 2000 characters'); return false; }
          if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { Swal.showValidationMessage('Invalid email format'); return false; }
          return {
            type: document.getElementById('swal-report-type').value,
            description: desc,
            email: email || undefined,
            buildContext: ReportManager._getBuildContext(state)
          };
        }
      }).then(function (result) {
        if (!result.isConfirmed || !result.value) return;
        ApiClient.submitReport(result.value).then(function () {
          Toast.success('Report submitted. Thank you!');
        }).catch(function (err) {
          Toast.error('Failed to submit: ' + (err.message || 'Unknown error'));
        });
      });
    }
  };

  /* ============================================================
   * ShareManager — Save/Load/Share via API
   * ============================================================ */
  var ShareManager = {
    save: function (state) {
      var payload = state.toApiPayload();
      if (state.state.buildId) payload.buildId = state.state.buildId;
      ApiClient.saveBuild(payload).then(function (res) {
        state.setBuildId(res.buildId || res.id);
        Toast.success('Build saved!');
      }).catch(function (err) {
        Toast.error('Save failed: ' + err.message);
      });
    },

    showLoadModal: function (state) {
      ApiClient.loadBuilds().then(function (res) {
        var builds = res.builds || res.data || [];
        if (!builds.length) {
          Toast.info('No saved builds found.');
          return;
        }
        var html = '<div class="pcb__load-list">';
        for (var i = 0; i < builds.length; i++) {
          var b = builds[i];
          var date = b.updatedAt ? new Date(b.updatedAt).toLocaleDateString('en-PH', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
          var compCount = b.componentCount || '';
          html += '<div class="pcb__load-item" data-build-id="' + b.id + '">' +
            '<div class="pcb__load-info">' +
              '<div class="pcb__load-name">' + esc(b.name) + '</div>' +
              '<div class="pcb__load-meta">' +
                '<span class="pcb__load-price">' + formatPrice(b.totalPrice || 0) + '</span>' +
                (b.tier ? '<span class="pcb__load-tier">' + esc(b.tier) + '</span>' : '') +
                (date ? '<span class="pcb__load-date">' + date + '</span>' : '') +
              '</div>' +
            '</div>' +
            '<div class="pcb__load-actions">' +
              '<button class="pcb__btn pcb__btn--sm pcb__load-btn" data-build-id="' + b.id + '">Load</button>' +
              '<button class="pcb__btn pcb__btn--sm pcb__delete-btn" data-build-id="' + b.id + '">' +
                '<svg style="width:14px;height:14px;" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>' +
              '</button>' +
            '</div>' +
          '</div>';
        }
        html += '</div>';

        if (typeof Swal !== 'undefined') {
          Swal.fire({
            title: 'Load Build',
            customClass: { popup: 'pcb-report-modal', backdrop: 'pcb-report-backdrop' },
            html: html,
            showConfirmButton: false,
            showCancelButton: true,
            cancelButtonText: 'Close',
            didOpen: function () {
              var loadBtns = document.querySelectorAll('.pcb__load-btn');
              for (var j = 0; j < loadBtns.length; j++) {
                loadBtns[j].addEventListener('click', function () {
                  var id = parseInt(this.getAttribute('data-build-id'), 10);
                  if (isNaN(id) || id <= 0) return;
                  Swal.close();
                  ShareManager._loadBuild(id, state);
                });
              }
              var delBtns = document.querySelectorAll('.pcb__delete-btn');
              for (var k = 0; k < delBtns.length; k++) {
                delBtns[k].addEventListener('click', function () {
                  var id = parseInt(this.getAttribute('data-build-id'), 10);
                  if (isNaN(id) || id <= 0) return;
                  var item = this.closest('.pcb__load-item');
                  ApiClient.deleteBuild(id).then(function () {
                    if (item) item.remove();
                    Toast.success('Build deleted.');
                  }).catch(function (err) { Toast.error(err.message); });
                });
              }
            }
          });
        }
      }).catch(function (err) {
        Toast.error('Failed to load builds: ' + err.message);
      });
    },

    _loadBuild: function (id, state) {
      ApiClient.loadBuild(id).then(function (res) {
        state.loadFromApi(res.build || res);
        // Auto-resolve missing variant IDs for loaded components
        var comps = state.state.components;
        var resolvePromises = [];
        for (var type in comps) {
          if (comps.hasOwnProperty(type) && comps[type]) {
            resolvePromises.push(CartIntegration._resolveVariantId(comps[type]));
          }
        }
        if (resolvePromises.length > 0) {
          Promise.all(resolvePromises).then(function () {
            Toast.success('Build loaded!');
          });
        } else {
          Toast.success('Build loaded!');
        }
      }).catch(function (err) {
        Toast.error('Failed to load build: ' + err.message);
      });
    },

    share: function (state) {
      if (!state.state.buildId) {
        // Save first, then share
        var payload = state.toApiPayload();
        ApiClient.saveBuild(payload).then(function (res) {
          state.setBuildId(res.buildId || res.id);
          ShareManager._doShare(state);
        }).catch(function (err) {
          Toast.error('Save failed: ' + err.message);
        });
        return;
      }
      this._doShare(state);
    },

    _doShare: function (state) {
      ApiClient.shareBuild(state.state.buildId).then(function (res) {
        var url = window.location.origin + window.location.pathname + '?share=' + res.token;
        if (typeof Swal !== 'undefined') {
          Swal.fire({
            title: 'Share Build',
            html: '<input type="text" class="swal2-input" value="' + esc(url) + '" readonly id="pcb-share-url" style="font-size:13px;">',
            confirmButtonText: 'Copy Link',
            didOpen: function () {
              document.getElementById('pcb-share-url').select();
            }
          }).then(function (result) {
            if (result.isConfirmed) {
              navigator.clipboard.writeText(url).then(function () {
                Toast.success('Link copied!');
              }).catch(function () {
                Toast.error('Failed to copy.');
              });
            }
          });
        }
      }).catch(function (err) {
        Toast.error('Share failed: ' + err.message);
      });
    },

    loadShared: function (token, state) {
      ApiClient.loadShared(token).then(function (res) {
        var buildData = res.build || res;
        state.loadFromApi(buildData);
        // Dynamic page title for shared builds (SEO + social sharing)
        if (buildData.name) {
          document.title = buildData.name + ' \u2014 PC Builder | EasyPC';
        }
        // Resolve variant IDs for Add to Cart (same as _loadBuild)
        var comps = state.state.components;
        var resolvePromises = [];
        for (var type in comps) {
          if (comps.hasOwnProperty(type) && comps[type]) {
            resolvePromises.push(CartIntegration._resolveVariantId(comps[type]));
          }
        }
        var onDone = function () {
          Toast.success('Shared build loaded!');
          // Clean URL
          var url = new URL(window.location);
          url.searchParams.delete('share');
          window.history.replaceState({}, '', url);
        };
        if (resolvePromises.length > 0) {
          Promise.all(resolvePromises).then(onDone);
        } else {
          onDone();
        }
      }).catch(function (err) {
        Toast.error('Failed to load shared build: ' + err.message);
      });
    }
  };

  /* ============================================================
   * DarkMode — scoped dark theme toggle
   * ============================================================ */
  var DarkMode = {
    _root: null,
    _isDark: false,

    init: function (rootEl) {
      this._root = rootEl;
      this._isDark = localStorage.getItem('pcb-dark-mode') === 'true';
      this.apply();
    },

    toggle: function () {
      this._isDark = !this._isDark;
      this.apply();
      localStorage.setItem('pcb-dark-mode', this._isDark);
    },

    isDark: function () {
      return this._isDark;
    },

    apply: function () {
      if (!this._root) return;
      this._root.classList.toggle('pcb--dark', this._isDark);

      // Toggle moon/sun icons
      var moonIcon = document.getElementById('pcb-dark-icon');
      var sunIcon = document.getElementById('pcb-light-icon');
      if (moonIcon) moonIcon.style.display = this._isDark ? 'none' : 'block';
      if (sunIcon) sunIcon.style.display = this._isDark ? 'block' : 'none';

      // Recolor SVG illustration
      this._recolorSVG();

      // Update section background
      var section = this._root.closest('.pc-builder-v3-section');
      if (section) {
        section.style.background = this._isDark ? '#1a1c1e' : '';
      }
    },

    _recolorSVG: function () {
      var dark = this._isDark;
      // Per-class recoloring (CSS can't override inline SVG fill attributes)
      var classMap = dark ? [
        { sel: '.svg-case-shell',     fill: '#333538', stroke: '#555' },
        { sel: '.svg-case-inner',     fill: '#222426', stroke: '#3a3c3e' },
        { sel: '.svg-io-panel',       fill: '#444648', stroke: '#555' },
        { sel: '.svg-cpu-ihs',        fill: '#444648' },
        { sel: '.svg-metal',          fill: '#444' },
        { sel: '.svg-psu-box',        fill: '#444648', stroke: '#555' },
        { sel: '.svg-psu-fan-center', fill: '#333538', stroke: '#555' },
        { sel: '.svg-psu-label',      fill: '#9a9a9a' },
        { sel: '.svg-metal-stroke',   stroke: '#555' },
        { sel: '.svg-case-feet',      fill: '#444' },
        { sel: '.svg-glass-edge',     stroke: 'rgba(255,255,255,0.08)' },
      ] : [
        { sel: '.svg-case-shell',     fill: '#e8e8e8', stroke: '#cccccc' },
        { sel: '.svg-case-inner',     fill: '#f0f0f0', stroke: '#dddddd' },
        { sel: '.svg-io-panel',       fill: '#e0e0e0', stroke: '#cccccc' },
        { sel: '.svg-cpu-ihs',        fill: '#e0e0e0' },
        { sel: '.svg-metal',          fill: '#cccccc' },
        { sel: '.svg-psu-box',        fill: '#d5d5d5', stroke: '#bbbbbb' },
        { sel: '.svg-psu-fan-center', fill: '#e0e0e0', stroke: '#cccccc' },
        { sel: '.svg-psu-label',      fill: '#666' },
        { sel: '.svg-metal-stroke',   stroke: '#cccccc' },
        { sel: '.svg-case-feet',      fill: '#cccccc' },
        { sel: '.svg-glass-edge',     stroke: 'rgba(255,255,255,0.5)' },
      ];

      classMap.forEach(function (item) {
        var els = document.querySelectorAll(item.sel);
        els.forEach(function (el) {
          if (item.fill) el.setAttribute('fill', item.fill);
          if (item.stroke) el.setAttribute('stroke', item.stroke);
        });
      });

      // I/O ports, vent lines, empty slots, pin dots, fan grille, cable ports
      var recolor = function (sel, attr, lightVal, darkVal) {
        document.querySelectorAll(sel).forEach(function (el) {
          el.setAttribute(attr, dark ? darkVal : lightVal);
        });
      };
      recolor('.svg-io-port', 'stroke', '#bbbbbb', '#666');
      recolor('.svg-vent line', 'stroke', '#cccccc', '#555');
      recolor('.pc-component.empty rect', 'stroke', '#bbbbbb', '#555');
      recolor('.pc-component.empty text', 'fill', '#bbbbbb', '#555');
      recolor('#pc-psu g[opacity="0.25"] circle', 'stroke', '#bbbbbb', '#555');
      recolor('#pc-psu g[opacity="0.3"] rect', 'stroke', '#bbbbbb', '#555');
      recolor('#pc-cpu g[opacity="0.25"] rect', 'fill', '#999', '#555');

      // Glass gradient
      var stops = document.querySelectorAll('#glassGradient stop');
      if (stops.length === 3) {
        var vals = dark
          ? ['rgba(255,255,255,0.08)', 'rgba(255,255,255,0.02)', 'rgba(255,255,255,0.05)']
          : ['rgba(255,255,255,0.4)', 'rgba(255,255,255,0.15)', 'rgba(255,255,255,0.3)'];
        for (var i = 0; i < 3; i++) stops[i].setAttribute('stop-color', vals[i]);
      }
    }
  };

  /* ============================================================
   * CenterPanel — Interactive SVG PC illustration
   * ============================================================ */
  var CenterPanel = {
    _container: null,
    _state: null,

    init: function (state) {
      this._state = state;
      this._container = document.getElementById('pcb-center');
      if (!this._container) return;
      this.render();
      var self = this;
      state.subscribe('components', function () { self._updateComponentStates(); });
      state.subscribe('totalPower', function () { self._updatePowerGauge(); });
    },

    render: function () {
      if (!this._container) return;
      var comps = this._state.state.components;
      var compCount = Object.keys(comps).length;
      var totalTypes = ComponentRegistry.coreTypes().length;

      this._container.innerHTML =
        '<div class="pcb__pc-container" id="pcb-pc-svg">' +
          this._buildSVG() +
        '</div>' +
        // Build progress
        '<div class="pcb__progress">' +
          '<div class="pcb__progress-text"><strong>' + compCount + '</strong> / ' + totalTypes + ' components' + (compCount > 0 && compCount < totalTypes ? ' &mdash; Almost there!' : compCount >= totalTypes ? ' &mdash; Complete!' : '') + '</div>' +
          '<div class="pcb__progress-track"><div class="pcb__progress-fill" id="pcb-progress-fill"></div></div>' +
        '</div>' +
        // Power gauge
        '<div class="pcb__power" id="pcb-power-gauge">' +
          this._renderPowerGauge() +
        '</div>' +
        // Compatibility disclaimer
        '<div class="pcb__disclaimer">' +
          '<svg style="width:14px;height:14px;flex-shrink:0;margin-top:1px" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>' +
          '<span>Compatibility checks are for guidance only and may not cover all scenarios. Please verify component specifications before purchasing.</span>' +
        '</div>';

      // Animate progress bar (0% → target after 300ms delay)
      var self = this;
      this._initialRender = true;
      setTimeout(function () {
        var fill = document.getElementById('pcb-progress-fill');
        if (fill) fill.style.width = Math.round(compCount / totalTypes * 100) + '%';
        self._initialRender = false;
      }, 300);

      this._updateComponentStates();
      this._updatePowerGauge();
    },

    _buildSVG: function () {
      return '<svg viewBox="0 0 400 540" xmlns="http://www.w3.org/2000/svg">' +
        '<defs>' +
          '<pattern id="pcbPattern" width="20" height="20" patternUnits="userSpaceOnUse">' +
            '<rect width="20" height="20" fill="transparent"/>' +
            '<circle cx="10" cy="10" r="0.5" fill="rgba(98,177,52,0.15)"/>' +
          '</pattern>' +
          '<linearGradient id="glassGradient" x1="0" y1="0" x2="1" y2="1">' +
            '<stop offset="0%" stop-color="rgba(255,255,255,0.4)"/>' +
            '<stop offset="50%" stop-color="rgba(255,255,255,0.15)"/>' +
            '<stop offset="100%" stop-color="rgba(255,255,255,0.3)"/>' +
          '</linearGradient>' +
          '<linearGradient id="compGreen" x1="0" y1="0" x2="1" y2="1">' +
            '<stop offset="0%" stop-color="#4a8a28"/>' +
            '<stop offset="100%" stop-color="#62B134"/>' +
          '</linearGradient>' +
          '<linearGradient id="compGreenLight" x1="0" y1="0" x2="1" y2="1">' +
            '<stop offset="0%" stop-color="#5ca030"/>' +
            '<stop offset="100%" stop-color="#7ed94a"/>' +
          '</linearGradient>' +
        '</defs>' +
        // Case shell
        '<rect class="svg-case-shell" x="20" y="10" width="360" height="520" rx="12" fill="#e8e8e8" stroke="#cccccc" stroke-width="2"/>' +
        '<rect class="svg-case-inner" x="30" y="20" width="340" height="500" rx="8" fill="#f0f0f0" stroke="#dddddd" stroke-width="1"/>' +
        '<rect x="30" y="20" width="340" height="500" rx="8" fill="url(#glassGradient)"/>' +
        // Top ventilation
        '<g class="svg-vent" opacity="0.25">' +
          '<line x1="60" y1="32" x2="340" y2="32" stroke="#cccccc" stroke-width="1"/>' +
          '<line x1="60" y1="36" x2="340" y2="36" stroke="#cccccc" stroke-width="1"/>' +
          '<line x1="60" y1="40" x2="340" y2="40" stroke="#cccccc" stroke-width="1"/>' +
        '</g>' +
        // I/O panel
        '<rect class="svg-io-panel" x="160" y="24" width="80" height="18" rx="3" fill="#e0e0e0" stroke="#cccccc" stroke-width="1"/>' +
        '<circle class="svg-io-port" cx="180" cy="33" r="3" fill="none" stroke="#bbbbbb" stroke-width="1"/>' +
        '<circle class="svg-io-port" cx="200" cy="33" r="3" fill="none" stroke="#bbbbbb" stroke-width="1"/>' +
        '<rect class="svg-io-port" x="212" y="30" width="14" height="6" rx="1" fill="none" stroke="#bbbbbb" stroke-width="1"/>' +
        // Motherboard
        '<g class="pc-component" id="pc-motherboard">' +
          '<rect x="55" y="55" width="290" height="400" rx="4" fill="#2d5a1a" stroke="#4a8a28" stroke-width="1.5"/>' +
          '<rect x="55" y="55" width="290" height="400" rx="4" fill="url(#pcbPattern)"/>' +
          '<g stroke="#5aaa30" stroke-width="0.8" opacity="0.4">' +
            '<path d="M100 100 L150 100 L150 140" fill="none"/>' +
            '<path d="M200 80 L200 120 L250 120 L250 160" fill="none"/>' +
            '<path d="M300 100 L300 200" fill="none"/>' +
            '<path d="M120 250 L180 250 L180 300" fill="none"/>' +
            '<path d="M250 300 L310 300 L310 350" fill="none"/>' +
            '<path d="M80 180 L130 180 L130 220 L180 220" fill="none"/>' +
          '</g>' +
          '<rect x="58" y="58" width="280" height="18" rx="2" fill="#3a6a22" stroke="#4a8a28" stroke-width="0.5"/>' +
          '<rect x="58" y="58" width="18" height="130" rx="2" fill="#3a6a22" stroke="#4a8a28" stroke-width="0.5"/>' +
        '</g>' +
        // CPU
        '<g class="pc-component" id="pc-cpu">' +
          '<rect class="svg-cpu-ihs" x="140" y="105" width="80" height="80" rx="4" fill="#e0e0e0" stroke="#4a8a28" stroke-width="1.5"/>' +
          '<rect x="150" y="115" width="60" height="60" rx="3" fill="url(#compGreen)" stroke="#7ed94a" stroke-width="1"/>' +
          '<text id="pc-cpu-line1" x="180" y="145" text-anchor="middle" fill="#fff" font-size="7" font-weight="700" font-family="Open Sans">CPU</text>' +
          '<text id="pc-cpu-line2" x="180" y="155" text-anchor="middle" fill="rgba(255,255,255,0.7)" font-size="5" font-family="Open Sans"></text>' +
          '<g opacity="0.25">' +
            '<rect x="145" y="110" width="2" height="2" fill="#999" rx="0.5"/>' +
            '<rect x="149" y="110" width="2" height="2" fill="#999" rx="0.5"/>' +
            '<rect x="153" y="110" width="2" height="2" fill="#999" rx="0.5"/>' +
            '<rect x="207" y="110" width="2" height="2" fill="#999" rx="0.5"/>' +
            '<rect x="211" y="110" width="2" height="2" fill="#999" rx="0.5"/>' +
            '<rect x="215" y="110" width="2" height="2" fill="#999" rx="0.5"/>' +
          '</g>' +
        '</g>' +
        // RAM
        '<g class="pc-component" id="pc-ram">' +
          '<rect x="245" y="90" width="14" height="100" rx="2" fill="#2d5a1a" stroke="#4a8a28" stroke-width="1"/>' +
          '<rect x="248" y="95" width="8" height="14" rx="1" fill="#4a8a28"/>' +
          '<rect x="248" y="112" width="8" height="14" rx="1" fill="#4a8a28"/>' +
          '<rect x="248" y="129" width="8" height="14" rx="1" fill="#4a8a28"/>' +
          '<rect x="245" y="88" width="14" height="4" rx="1" fill="url(#compGreenLight)"/>' +
          '<rect x="265" y="90" width="14" height="100" rx="2" fill="#2d5a1a" stroke="#4a8a28" stroke-width="1"/>' +
          '<rect x="268" y="95" width="8" height="14" rx="1" fill="#4a8a28"/>' +
          '<rect x="268" y="112" width="8" height="14" rx="1" fill="#4a8a28"/>' +
          '<rect x="268" y="129" width="8" height="14" rx="1" fill="#4a8a28"/>' +
          '<rect x="265" y="88" width="14" height="4" rx="1" fill="url(#compGreenLight)"/>' +
          '<text id="pc-ram-line1" x="262" y="155" text-anchor="middle" fill="#fff" font-size="5" font-weight="700" font-family="Open Sans" opacity="0.9"></text>' +
          '<text id="pc-ram-line2" x="262" y="163" text-anchor="middle" fill="rgba(255,255,255,0.6)" font-size="4" font-family="Open Sans"></text>' +
        '</g>' +
        // GPU
        '<g class="pc-component" id="pc-gpu">' +
          '<rect class="svg-metal" x="340" y="240" width="5" height="110" rx="1" fill="#cccccc" stroke="#bbbbbb" stroke-width="0.5"/>' +
          '<rect x="70" y="240" width="270" height="30" rx="3" fill="#2d5a1a" stroke="#4a8a28" stroke-width="1"/>' +
          '<rect class="svg-metal" x="70" y="230" width="270" height="12" rx="2" fill="#d0d0d0" stroke="#bbbbbb" stroke-width="0.5"/>' +
          '<rect x="70" y="272" width="270" height="75" rx="5" fill="#3a6a22" stroke="#4a8a28" stroke-width="1.5"/>' +
          '<circle cx="135" cy="310" r="28" fill="none" stroke="#5aaa30" stroke-width="1.5"/>' +
          '<circle cx="135" cy="310" r="5" fill="#5aaa30"/>' +
          '<circle cx="210" cy="310" r="28" fill="none" stroke="#5aaa30" stroke-width="1.5"/>' +
          '<circle cx="210" cy="310" r="5" fill="#5aaa30"/>' +
          '<circle cx="285" cy="310" r="28" fill="none" stroke="#5aaa30" stroke-width="1.5"/>' +
          '<circle cx="285" cy="310" r="5" fill="#5aaa30"/>' +
          '<text id="pc-gpu-label" x="210" y="258" text-anchor="middle" fill="#fff" font-size="8" font-weight="700" font-family="Open Sans" letter-spacing="1">GPU</text>' +
        '</g>' +
        // SSD
        '<g class="pc-component" id="pc-ssd">' +
          '<rect x="100" y="200" width="60" height="16" rx="2" fill="#3a6a22" stroke="#4a8a28" stroke-width="1"/>' +
          '<rect x="110" y="203" width="20" height="10" rx="1" fill="#5aaa30"/>' +
          '<text id="pc-ssd-label" x="142" y="211" text-anchor="middle" fill="#fff" font-size="4" font-family="Open Sans" opacity="0.8">SSD</text>' +
          '<rect x="98" y="197" width="64" height="4" rx="1" fill="#4a8a28" stroke="#5aaa30" stroke-width="0.5"/>' +
        '</g>' +
        // PSU
        '<g class="pc-component" id="pc-psu">' +
          '<line class="svg-metal-stroke" x1="55" y1="380" x2="345" y2="380" stroke="#cccccc" stroke-width="1.5"/>' +
          '<rect class="svg-psu-box" x="60" y="390" width="160" height="100" rx="4" fill="#d5d5d5" stroke="#bbbbbb" stroke-width="1.5"/>' +
          '<circle class="svg-metal-stroke" cx="140" cy="440" r="35" fill="none" stroke="#cccccc" stroke-width="1"/>' +
          '<circle class="svg-metal-stroke" cx="140" cy="440" r="25" fill="none" stroke="#cccccc" stroke-width="0.8"/>' +
          '<circle class="svg-psu-fan-center" cx="140" cy="440" r="8" fill="#e0e0e0" stroke="#cccccc" stroke-width="0.5"/>' +
          '<g opacity="0.25"><circle cx="140" cy="440" r="30" fill="none" stroke="#bbbbbb" stroke-width="0.5"/><circle cx="140" cy="440" r="20" fill="none" stroke="#bbbbbb" stroke-width="0.5"/><circle cx="140" cy="440" r="15" fill="none" stroke="#bbbbbb" stroke-width="0.5"/></g>' +
          '<text id="pc-psu-line1" class="svg-psu-label" x="140" y="435" text-anchor="middle" dominant-baseline="middle" fill="#666" font-size="6" font-weight="600" font-family="Open Sans">PSU</text>' +
          '<text id="pc-psu-line2" x="140" y="446" text-anchor="middle" dominant-baseline="middle" fill="rgba(102,102,102,0.7)" font-size="5" font-family="Open Sans"></text>' +
          '<g opacity="0.3"><rect x="225" y="400" width="8" height="6" rx="1" fill="none" stroke="#bbbbbb" stroke-width="0.5"/><rect x="225" y="410" width="8" height="6" rx="1" fill="none" stroke="#bbbbbb" stroke-width="0.5"/><rect x="225" y="420" width="8" height="6" rx="1" fill="none" stroke="#bbbbbb" stroke-width="0.5"/></g>' +
        '</g>' +
        // Cooler (empty)
        '<g class="pc-component empty" id="pc-cooler">' +
          '<rect x="130" y="80" width="100" height="20" rx="3" fill="none" stroke="#bbbbbb" stroke-width="1" stroke-dasharray="4 3"/>' +
          '<text x="180" y="93" text-anchor="middle" fill="#bbbbbb" font-size="6" font-family="Open Sans">+ COOLER</text>' +
        '</g>' +
        // HDD (empty)
        '<g class="pc-component empty" id="pc-hdd">' +
          '<rect x="250" y="400" width="80" height="50" rx="3" fill="none" stroke="#bbbbbb" stroke-width="1" stroke-dasharray="4 3"/>' +
          '<text x="290" y="428" text-anchor="middle" fill="#bbbbbb" font-size="6" font-family="Open Sans">+ HDD</text>' +
        '</g>' +
        // Glass edge + feet
        '<rect class="svg-glass-edge" x="30" y="20" width="340" height="500" rx="8" fill="none" stroke="rgba(255,255,255,0.5)" stroke-width="1"/>' +
        '<rect class="svg-case-feet" x="50" y="525" width="30" height="6" rx="3" fill="#cccccc"/>' +
        '<rect class="svg-case-feet" x="320" y="525" width="30" height="6" rx="3" fill="#cccccc"/>' +
      '</svg>';
    },

    _updateComponentStates: function () {
      var comps = this._state.state.components;
      var compCount = Object.keys(comps).length;
      var totalTypes = ComponentRegistry.coreTypes().length;

      // Update filled/empty classes on SVG component groups
      var typeToSvgId = {
        cpu: 'pc-cpu', motherboard: 'pc-motherboard', ram: 'pc-ram',
        gpu: 'pc-gpu', ssd: 'pc-ssd', psu: 'pc-psu',
        cooler: 'pc-cooler', hdd: 'pc-hdd', 'case': 'pc-case'
      };
      for (var type in typeToSvgId) {
        var el = document.getElementById(typeToSvgId[type]);
        if (el) {
          if (comps[type]) {
            el.classList.remove('empty');
            el.classList.add('filled', 'breathe');
          } else {
            el.classList.remove('filled', 'breathe');
            el.classList.add('empty');
          }
        }
      }

      // Update SVG text labels with selected component names
      this._updateSVGLabels(comps);

      // Update progress bar (skip during initial render — let setTimeout animate it)
      if (!this._initialRender) {
        var fill = document.getElementById('pcb-progress-fill');
        if (fill) fill.style.width = Math.round(compCount / totalTypes * 100) + '%';
      }
      var text = this._container ? this._container.querySelector('.pcb__progress-text') : null;
      if (text) text.innerHTML = '<strong>' + compCount + '</strong> / ' + totalTypes + ' components' + (compCount > 0 && compCount < totalTypes ? ' &mdash; Almost there!' : compCount >= totalTypes ? ' &mdash; Complete!' : '');

      // Reapply SVG dark mode colors
      if (DarkMode.isDark()) DarkMode._recolorSVG();
    },

    _renderPowerGauge: function () {
      var s = this._state.state;
      var psu = s.components.psu;
      var psuWatts = psu ? (parseInt(psu.psuWattage) || 0) : 0;
      var totalPower = s.totalPower || 0;
      var pct = psuWatts > 0 ? Math.round(totalPower / psuWatts * 100) : 0;
      var fillClass = pct >= 80 ? 'pcb__power-fill--danger' : pct >= 60 ? 'pcb__power-fill--warn' : '';

      return '<div class="pcb__power-header">' +
        '<div class="pcb__power-label">&#9889; Estimated Power Draw</div>' +
        '<div class="pcb__power-value"><span>~' + totalPower + 'W</span>' + (psuWatts ? ' / ' + psuWatts + 'W' : '') + '</div>' +
      '</div>' +
      '<div class="pcb__power-track">' +
        '<div class="pcb__power-fill ' + fillClass + '" id="pcb-power-fill" data-target-width="' + pct + '"></div>' +
        (psuWatts ? '<div class="pcb__power-threshold" style="left:80%;" title="80% recommended max"></div>' : '') +
      '</div>' +
      (psuWatts ? '<div class="pcb__power-labels"><span>0W</span><span style="color:var(--pcb-orange);">' + Math.round(psuWatts * 0.8) + 'W (80%)</span><span>' + psuWatts + 'W</span></div>' : '');
    },

    _updatePowerGauge: function () {
      var gauge = document.getElementById('pcb-power-gauge');
      if (gauge) {
        gauge.innerHTML = this._renderPowerGauge();
        // Animate power fill from 0 to target width
        setTimeout(function () {
          var fill = document.getElementById('pcb-power-fill');
          if (fill) fill.style.width = fill.getAttribute('data-target-width') + '%';
        }, 500);
      }
    },

    _updateSVGLabels: function (comps) {
      // Helper to shorten product names for SVG display
      function shortName(name, maxLen) {
        if (!name) return '';
        // Remove brand prefixes and common filler words
        var short = name
          .replace(/^(AMD|Intel|NVIDIA|ASUS|MSI|Gigabyte|ASRock|Corsair|G\.?Skill|Kingston|Samsung|Western Digital|WD|Seagate|EVGA|Cooler Master|NZXT|be quiet!|Thermaltake|Seasonic|DeepCool|RAKK)\s+/i, '')
          .replace(/Desktop Processor.*$/i, '')
          .replace(/\s+with\s+.*$/i, '')
          .replace(/\s+Edition$/i, '');
        if (short.length > (maxLen || 20)) short = short.substring(0, maxLen || 20) + '…';
        return short;
      }

      // CPU: 2-line label (model + sub-model)
      var cpuL1 = document.getElementById('pc-cpu-line1');
      var cpuL2 = document.getElementById('pc-cpu-line2');
      if (cpuL1) {
        if (comps.cpu) {
          var cpuName = comps.cpu.title || comps.cpu.name || '';
          // Try to extract brand line + model (e.g. "RYZEN 9" + "7900X")
          var cpuMatch = cpuName.match(/(Ryzen \d|Core i\d|Athlon|Pentium)/i);
          if (cpuMatch) {
            cpuL1.textContent = cpuMatch[1].toUpperCase();
            var rest = cpuName.substring(cpuName.indexOf(cpuMatch[1]) + cpuMatch[1].length).trim().split(/\s/)[0];
            if (cpuL2) cpuL2.textContent = rest;
          } else {
            cpuL1.textContent = shortName(cpuName, 12);
            if (cpuL2) cpuL2.textContent = '';
          }
        } else {
          cpuL1.textContent = 'CPU';
          if (cpuL2) cpuL2.textContent = '';
        }
      }

      // GPU: single line
      var gpuLabel = document.getElementById('pc-gpu-label');
      if (gpuLabel) {
        if (comps.gpu) {
          var gpuName = comps.gpu.title || comps.gpu.name || '';
          // Extract model (RTX 4080 SUPER, RX 7900 XTX, etc.)
          var gpuMatch = gpuName.match(/((?:RTX|GTX|RX|Arc)\s*\w+(?:\s+(?:SUPER|Ti|XT|XTX))?)/i);
          gpuLabel.textContent = gpuMatch ? gpuMatch[1].toUpperCase() : shortName(gpuName, 18);
        } else {
          gpuLabel.textContent = 'GPU';
        }
      }

      // SSD: single line
      var ssdLabel = document.getElementById('pc-ssd-label');
      if (ssdLabel) {
        if (comps.ssd) {
          var ssdName = comps.ssd.title || comps.ssd.name || '';
          var ssdShort = shortName(ssdName, 12);
          ssdLabel.textContent = ssdShort;
        } else {
          ssdLabel.textContent = 'SSD';
        }
      }

      // RAM: 2-line (capacity + type)
      var ramL1 = document.getElementById('pc-ram-line1');
      var ramL2 = document.getElementById('pc-ram-line2');
      if (ramL1) {
        if (comps.ram) {
          var ramName = comps.ram.title || comps.ram.name || '';
          var capMatch = ramName.match(/(\d+\s*GB)/i);
          var typeMatch = ramName.match(/(DDR\d)/i);
          ramL1.textContent = capMatch ? capMatch[1] : shortName(ramName, 8);
          if (ramL2) ramL2.textContent = typeMatch ? typeMatch[1] : '';
        } else {
          ramL1.textContent = '';
          if (ramL2) ramL2.textContent = '';
        }
      }

      // PSU: 2-line (model + wattage/rating)
      var psuL1 = document.getElementById('pc-psu-line1');
      var psuL2 = document.getElementById('pc-psu-line2');
      if (psuL1) {
        if (comps.psu) {
          var psuName = comps.psu.title || comps.psu.name || '';
          var psuModel = shortName(psuName, 14);
          var wattMatch = psuName.match(/(\d+)\s*W/i);
          var ratingMatch = psuName.match(/(80\+\s*\w+)/i);
          psuL1.textContent = psuModel;
          if (psuL2) {
            var line2Parts = [];
            if (wattMatch) line2Parts.push(wattMatch[1] + 'W');
            if (ratingMatch) line2Parts.push(ratingMatch[1]);
            psuL2.textContent = line2Parts.join(' ');
          }
        } else {
          psuL1.textContent = 'PSU';
          if (psuL2) psuL2.textContent = '';
        }
      }
    }
  };

  /* ============================================================
   * UI.BudgetBar — Budget progress bar below toolbar
   * ============================================================ */
  var BudgetBar = {
    _state: null,
    _budget: 200000,

    init: function (state) {
      this._state = state;
      // Load budget from localStorage
      var saved = localStorage.getItem('pcb-budget');
      if (saved) this._budget = parseInt(saved) || 200000;
      var self = this;
      state.subscribe('components', function () { self.update(); });
      // Delay initial update so CSS transition animates from 0%
      setTimeout(function () { self.update(); }, 200);
    },

    update: function () {
      var fill = document.getElementById('pcb-budget-fill');
      var amount = document.getElementById('pcb-budget-amount');
      if (!fill || !amount) return;

      var total = this._state.state.totalPrice || 0;
      var pct = Math.min(100, Math.round(total / this._budget * 100));

      fill.style.width = pct + '%';
      fill.className = 'pcb__budget-fill';
      if (total > this._budget) fill.classList.add('pcb__budget-fill--over');
      else if (pct >= 80) fill.classList.add('pcb__budget-fill--warn');

      amount.innerHTML = formatPrice(total) + ' <span>/ ' + formatPrice(this._budget) + '</span>';
    }
  };

  /* ============================================================
   * UI.WizardStepper — Build progress stepper
   * ============================================================ */
  var WizardStepper = {
    _state: null,
    _container: null,

    init: function (state) {
      this._state = state;
      this._container = document.getElementById('pcb-wizard');
      var self = this;
      state.subscribe('components', function () { self.render(); });
      this.render();
    },

    render: function () {
      if (!this._container) return;
      var comps = this._state.state.components;
      var html = '<div class="pcb__wizard-inner">';
      var foundActive = false;

      for (var i = 0; i < WIZARD_STEPS.length; i++) {
        var step = WIZARD_STEPS[i];
        // Check if all types in this step have components
        var allFilled = true;
        var anyFilled = false;
        for (var t = 0; t < step.types.length; t++) {
          if (comps[step.types[t]]) anyFilled = true;
          else allFilled = false;
        }

        var stepClass = 'pcb__wizard-step';
        var dotContent = '' + (i + 1);
        if (allFilled && anyFilled) {
          stepClass += ' pcb__wizard-step--completed';
          dotContent = '\u2713';
        } else if (!foundActive) {
          stepClass += ' pcb__wizard-step--active';
          foundActive = true;
        } else {
          stepClass += ' pcb__wizard-step--upcoming';
        }

        // Add connector before step (except first)
        if (i > 0) {
          var connClass = 'pcb__wizard-connector';
          // Previous step was completed
          var prevStep = WIZARD_STEPS[i - 1];
          var prevAllFilled = true;
          for (var p = 0; p < prevStep.types.length; p++) {
            if (!comps[prevStep.types[p]]) { prevAllFilled = false; break; }
          }
          if (prevAllFilled) connClass += ' pcb__wizard-connector--done';
          html += '<div class="' + connClass + '"></div>';
        }

        html += '<div class="' + stepClass + '" data-step="' + i + '">' +
          '<div class="pcb__wizard-dot">' + dotContent + '</div>' +
          '<span>' + esc(step.label) + '</span>' +
        '</div>';
      }

      html += '</div>';
      this._container.innerHTML = html;

      // Bind click events on steps
      var self = this;
      var stepEls = this._container.querySelectorAll('.pcb__wizard-step');
      for (var s = 0; s < stepEls.length; s++) {
        stepEls[s].addEventListener('click', function () {
          var idx = parseInt(this.getAttribute('data-step'));
          var step = WIZARD_STEPS[idx];
          if (step && step.types.length > 0) {
            // Open drawer for first empty type in this step
            var comps = self._state.state.components;
            for (var t = 0; t < step.types.length; t++) {
              if (!comps[step.types[t]]) {
                ProductDrawer.open(step.types[t]);
                return;
              }
            }
            // All filled, open first type for replacement
            ProductDrawer.open(step.types[0]);
          }
        });
      }
    }
  };

  /* ============================================================
   * App — Orchestrator
   * ============================================================ */
  var App = {
    _state: null,
    _rootEl: null,

    init: function (config) {
      var rootEl = document.getElementById(config.rootId || 'pc-builder-v3');
      if (!rootEl) return;

      this._rootEl = rootEl;
      this._state = new BuildState();

      // Init API client
      ApiClient.init(config.apiUrl || '');

      // Load saved state
      this._state.loadFromStorage();

      // Init dark mode (before rendering so correct theme applies)
      DarkMode.init(rootEl);

      // Render layout
      Layout.init(rootEl, this._state);
      ComponentSlotList.init(this._state);
      BudgetBar.init(this._state);
      WizardStepper.init(this._state);
      CenterPanel.init(this._state);
      ProductDrawer.init(this._state);
      BuildSummary.init(this._state);

      // Hide SEO static content once JS app is loaded
      var seoContent = document.getElementById('pcb-seo-content');
      if (seoContent) seoContent.classList.add('pcb-js-loaded');

      // Check for shared build token in URL
      var params = new URLSearchParams(window.location.search);
      var shareToken = params.get('share');
      if (shareToken && !/^[a-f0-9]{64}$/.test(shareToken)) {
        shareToken = null;
      }
      if (shareToken) {
        ShareManager.loadShared(shareToken, this._state);
      }

      // Ensure auth on load (non-blocking)
      ApiClient.ensureAuth().catch(function () { /* silent */ });

      // Mobile bottom bar
      this._setupMobile();
      var appSelf = this;
      window.addEventListener('resize', function () {
        appSelf._setupMobile();
      });
    },

    _setupMobile: function () {
      if (window.innerWidth >= 768) return;
      // Prevent duplicate tab bar
      if (document.querySelector('.pcb__mobile-tabs')) return;

      var self = this;
      var leftPanel = document.querySelector('.pcb__left');
      var centerPanel = document.querySelector('.pcb__center');
      var rightPanel = document.querySelector('.pcb__right');
      if (!leftPanel || !centerPanel || !rightPanel) return;

      // --- Tab bar ---
      var tabBar = document.createElement('div');
      tabBar.className = 'pcb__mobile-tabs';
      tabBar.innerHTML =
        '<div class="pcb__mobile-tab pcb__mobile-tab--active" data-tab="build">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>' +
          '<span>Build</span>' +
        '</div>' +
        '<div class="pcb__mobile-tab" data-tab="pc">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>' +
          '<span>PC</span>' +
        '</div>' +
        '<div class="pcb__mobile-tab" data-tab="summary">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>' +
          '<span>Summary</span>' +
        '</div>';
      var pcbRoot = document.querySelector('.pcb');
      (pcbRoot || document.body).appendChild(tabBar);

      // Map tabs to panels
      var panelMap = { build: leftPanel, pc: centerPanel, summary: rightPanel };
      var tabEls = tabBar.querySelectorAll('.pcb__mobile-tab');
      var summaryTab = tabBar.querySelector('[data-tab="summary"]');

      // Set initial active tab
      leftPanel.classList.add('pcb__tab--active');

      function switchTab(name) {
        // Deactivate all
        for (var key in panelMap) {
          panelMap[key].classList.remove('pcb__tab--active');
        }
        for (var i = 0; i < tabEls.length; i++) {
          tabEls[i].classList.remove('pcb__mobile-tab--active');
        }
        // Activate selected
        if (panelMap[name]) panelMap[name].classList.add('pcb__tab--active');
        var activeTab = tabBar.querySelector('[data-tab="' + name + '"]');
        if (activeTab) activeTab.classList.add('pcb__mobile-tab--active');
        // Clear badge when switching to summary
        if (name === 'summary' && summaryTab) {
          summaryTab.classList.remove('pcb__mobile-tab--badge');
        }
      }

      // Tab click handlers
      for (var i = 0; i < tabEls.length; i++) {
        tabEls[i].addEventListener('click', function () {
          switchTab(this.getAttribute('data-tab'));
        });
      }

      // --- Warning badge on Summary tab ---
      if (this._state) {
        this._state.subscribe('warnings', function () {
          var warnings = self._state.state.warnings;
          var activeTab = tabBar.querySelector('.pcb__mobile-tab--active');
          var isOnSummary = activeTab && activeTab.getAttribute('data-tab') === 'summary';
          if (warnings.length > 0 && !isOnSummary && summaryTab) {
            summaryTab.classList.add('pcb__mobile-tab--badge');
          } else if (summaryTab) {
            summaryTab.classList.remove('pcb__mobile-tab--badge');
          }
        });
      }

      // --- Drawer swipe-to-close gesture ---
      this._setupDrawerSwipe();
    },

    _setupDrawerSwipe: function () {
      var drawer = document.querySelector('.pcb__drawer');
      if (!drawer) return;
      var startX = 0, currentX = 0, dragging = false;

      drawer.addEventListener('touchstart', function (e) {
        startX = e.touches[0].clientX;
        currentX = startX;
        dragging = true;
      }, { passive: true });

      drawer.addEventListener('touchmove', function (e) {
        if (!dragging) return;
        currentX = e.touches[0].clientX;
        var dx = currentX - startX;
        if (dx > 0) {
          drawer.style.transform = 'translateX(' + dx + 'px)';
          drawer.style.transition = 'none';
        }
      }, { passive: true });

      drawer.addEventListener('touchend', function () {
        if (!dragging) return;
        dragging = false;
        var dx = currentX - startX;
        drawer.style.transition = '';
        drawer.style.transform = '';
        if (dx > 80) {
          ProductDrawer.close();
        }
      });
    },

    destroy: function () {
      if (this._rootEl) this._rootEl.innerHTML = '';
      var bar = document.querySelector('.pcb__mobile-bar');
      if (bar) bar.remove();
      var tabs = document.querySelector('.pcb__mobile-tabs');
      if (tabs) tabs.remove();
      var drawer = document.querySelector('.pcb__drawer');
      if (drawer) drawer.remove();
      var overlay = document.querySelector('.pcb__drawer-overlay');
      if (overlay) overlay.remove();
      var peek = document.querySelector('.pcb__drawer-peek');
      if (peek) peek.remove();
    }
  };

  /* ============================================================
   * Export to window namespace
   * ============================================================ */
  window.PCBuilderV3 = {
    App: App,
    BuildState: BuildState,
    ComponentRegistry: ComponentRegistry,
    ApiClient: ApiClient,
    CompatibilityEngine: CompatibilityEngine,
    PowerCalculator: PowerCalculator,
    FilterEngine: FilterEngine,
    CartIntegration: CartIntegration,
    RedditExport: RedditExport,
    ShareManager: ShareManager,
    Toast: Toast,
    DarkMode: DarkMode,
    CenterPanel: CenterPanel,
    ProductDrawer: ProductDrawer,
    BudgetBar: BudgetBar,
    WizardStepper: WizardStepper,
  };
})();
